/**
 * ConversationFunnel - Premium messaging-style lead capture
 * 
 * v7.0 - Compact-to-Expanded + Fixed Response Region
 * 
 * Key behaviors:
 * - Starts COMPACT (header + message + chips only)
 * - EXPANDS after first response into stable chat layout
 * - Response area is FIXED height (never resizes)
 * - Divider between thread and response is FIXED
 * - Keyboard does not shift container horizontally
 * - Booking renders inside same shell
 * 
 * @module ConversationFunnel
 */

// MA-PHASE11A-FINAL-CONSOLE-CLEANUP-023: Dev-only debug flag
const IS_DEV = process.env.NODE_ENV === 'development';

import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Check, 
  ArrowUp, 
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { QuickBookingWidget } from './booking';
// MA-UI-108: Import ZIP validation for semantic ZIP input type
import { validateZipCode, formatZipInput } from '../utils/zipValidation';
// MA-THEME-WIDGET-MIGRATION-012: Canonical theme system
import { resolveTheme, themeToFunnelAppearance, hexToRgb, contrastSafe } from '../lib/themeEngine';
import { convertThemeToLpTokens, resolveThemeTokens } from '../lib/themeAdapters';  // MA-186 + MA-298
// MA-PPB-CONSENT-HEADER-078: Shared header icon system
import { HEADER_ICON_MAP, resolveHeaderIcon, getHeaderIconType } from '../lib/headerIcons';
// MA-PPB-FUNNEL-RENDER-LOAD-084: High-fidelity skeleton for stable first paint
import FunnelSkeleton from './FunnelSkeleton';

const API_URL = window.location.origin || '';

// ============================================================
// Interactive Scroll Indicator Component
// Shows when options overflow, tappable to scroll up/down
// ============================================================
// ============================================================
// Scroll Indicator - Dual buttons (down at bottom, up at top)
// ============================================================
const ScrollIndicator = ({ show, direction, onTap, position, tokens }) => {
  if (!show) return null;
  
  const isDown = direction === 'down';
  
  return (
    <motion.button
      className={`lp-df-scroll-btn absolute left-3 pointer-events-auto z-20 cursor-pointer touch-manipulation
        ${position === 'bottom' ? 'bottom-3' : 'top-3'}`}
      data-testid={`scroll-indicator-${direction}`}
      onClick={onTap}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.2 }}
      whileTap={{ scale: 0.9 }}
      style={{
        background: tokens.scrollIndicatorBg,
        borderRadius: '50%',
        width: '36px',
        height: '36px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        border: tokens.scrollIndicatorBorder,
        boxShadow: tokens.surfaceMode === 'dark' 
          ? '0 2px 8px rgba(0,0,0,0.3)' 
          : '0 2px 8px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.08)'
      }}
    >
      <motion.div
        className="flex items-center justify-center"
        animate={{ y: isDown ? [0, 3, 0] : [0, -3, 0] }}
        transition={{ duration: 1.0, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg width="16" height="20" viewBox="0 0 16 20" fill="none"
          style={{ transform: isDown ? 'rotate(0deg)' : 'rotate(180deg)' }}>
          <path d="M2 4L8 10L14 4" stroke={tokens.scrollArrowColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 10L8 16L14 10" stroke={tokens.scrollArrowColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6"/>
        </svg>
      </motion.div>
    </motion.button>
  );
};

// ============================================================
// Bottom/Top Gradient Fade - Shows when content extends beyond fold
// ============================================================
const GradientFade = ({ show, position, tokens }) => {
  if (!show) return null;
  
  const isBottom = position === 'bottom';
  const fadeRgb = tokens.gradientFadeRgb;
  
  return (
    <motion.div
      className={`absolute left-0 right-0 h-6 pointer-events-none z-10
        ${isBottom ? 'bottom-0' : 'top-0'}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      style={{
        background: isBottom 
          ? `linear-gradient(to bottom, rgba(${fadeRgb},0), rgba(${fadeRgb},0.95))`
          : `linear-gradient(to top, rgba(${fadeRgb},0), rgba(${fadeRgb},0.95))`
      }}
    />
  );
};

// ============================================================
// Layout Constants
// ============================================================
const LAYOUT = {
  HEADER_HEIGHT: 56,
  // MA-ARCH-187B: Two-state funnel system
  // Compact: preview mode — first message + question + options
  COMPACT_THREAD_HEIGHT: 120,
  COMPACT_CARD_TOTAL_HEIGHT: 260,
  // Expanded: full interactive mode after first interaction
  EXPANDED_TOTAL_CONTENT_HEIGHT: 400,
  // Response area heights
  RESPONSE_AREA_HEIGHT_CHOICES: 180,
  RESPONSE_AREA_HEIGHT_INPUT: 80,
  CONTAINER_WIDTH: 440,
};

// ============================================================
// Design System Tokens — MA-ARCH-187 + MA-TYPO-001
// Token-driven typography via --ma-typo-* CSS variables
// Consistent spacing, sizing, and typography
// ============================================================
const DS = {
  // Spacing scale
  space: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    '2xl': 24,
  },
  // Typography — now references --ma-typo-* CSS vars (with fallbacks)
  text: {
    question: { size: 'var(--ma-typo-body-lg-size, 16px)', weight: 'var(--ma-typo-weight-medium, 500)', lineHeight: 'var(--ma-typo-body-lg-lh, 24px)', tracking: 'var(--ma-typo-tracking-tight, -0.01em)' },
    option: { size: 'var(--ma-typo-body-md-size, 14px)', weight: 'var(--ma-typo-weight-medium, 500)', lineHeight: 'var(--ma-typo-body-md-lh, 20px)' },
    ack: { size: 'var(--ma-typo-body-md-size, 14px)', weight: 'var(--ma-typo-weight-regular, 400)', lineHeight: 'var(--ma-typo-body-md-lh, 20px)' },
    body: { size: 'var(--ma-typo-body-lg-size, 16px)', weight: 'var(--ma-typo-weight-regular, 400)', lineHeight: 'var(--ma-typo-body-lg-lh, 24px)' },
    input: { size: '16px', weight: 'var(--ma-typo-weight-regular, 400)' },  // 16px fixed to prevent iOS zoom
    small: { size: 'var(--ma-typo-body-sm-size, 12px)', weight: 'var(--ma-typo-weight-regular, 400)', lineHeight: 'var(--ma-typo-body-sm-lh, 18px)' },
    heading: { size: 'var(--ma-typo-heading-sm-size, 20px)', weight: 'var(--ma-typo-weight-semibold, 600)', lineHeight: 'var(--ma-typo-heading-sm-lh, 28px)' },
    label: { size: 'var(--ma-typo-label-size, 14px)', weight: 'var(--ma-typo-weight-medium, 500)', lineHeight: 'var(--ma-typo-label-lh, 20px)' },
    caption: { size: 'var(--ma-typo-caption-size, 12px)', weight: 'var(--ma-typo-weight-regular, 400)', lineHeight: 'var(--ma-typo-caption-lh, 16px)' },
  },
  // Sizing
  bubble: {
    maxWidth: '78%',
    padding: '12px 16px',
    radius: '18px',
    radiusTail: '4px',
  },
  option: {
    height: 36,
    paddingX: 16,
    paddingY: 8,
    radius: '16px',
    radiusTail: '6px',
    gap: 8,
    minWidthCompact: 72,
    minWidthStandard: 140,
    maxWidth: '88%',
  },
  input: {
    height: 48,
    radius: '10px',  // Reduced from 14px for more rectangular appearance matching FB Test Page
    buttonSize: 44,
  },
};

// ============================================================
// MA-ARCH-187B-REFINEMENT: Compact Context Resolution System
// Derives header title, icon, invitation message, and CTA
// from funnel metadata — never uses arbitrary/generic fallbacks
// ============================================================
const CONTEXT_PATTERNS = [
  { keywords: ['savings', 'save', 'insurance', 'rate', 'price', 'cost', 'quote', 'premium', 'overpay'],
    title: 'Quick Savings Check', icon: 'trending-up',
    invitation: 'See if you qualify for better rates — takes 60 seconds',
    cta: 'Check Now' },
  { keywords: ['eligib', 'qualify', 'assessment', 'pre-qual', 'approved'],
    title: 'Eligibility Check', icon: 'check-circle',
    invitation: 'Find out if you qualify — quick and confidential',
    cta: 'Check Eligibility' },
  { keywords: ['coverage', 'protect', 'plan', 'policy', 'insured'],
    title: 'Coverage Check', icon: 'shield',
    invitation: 'See what coverage options fit your needs',
    cta: 'Review Options' },
  { keywords: ['health', 'medical', 'wellness', 'care'],
    title: 'Quick Health Check', icon: 'activity',
    invitation: 'Answer a few quick questions to get started',
    cta: 'Get Started' },
  { keywords: ['home', 'house', 'property', 'mortgage', 'homeowner'],
    title: 'Quick Home Check', icon: 'home',
    invitation: 'See what options are available for your home',
    cta: 'Start Check' },
  { keywords: ['compare', 'match', 'find', 'search', 'discover'],
    title: 'Quick Match', icon: 'search',
    invitation: 'We\'ll match you with the best options available',
    cta: 'Find My Match' },
  { keywords: ['schedule', 'book', 'appointment', 'consult'],
    title: 'Schedule a Check', icon: 'calendar',
    invitation: 'Book your free consultation in seconds',
    cta: 'Get Started' },
];

const CONTEXT_DEFAULT = {
  title: 'Quick Check',
  icon: 'check-circle',
  invitation: 'Answer a few quick questions to get started',
  cta: 'Get Started'
};

function resolveCompactContext(funnelName, firstMessage, appearance, header) {
  // MA-256: Read from canonical header object, NOT appearance (legacy)
  // appearance.title and appearance.selectedIcon are obsolete fields
  const explicitTitle = header?.title?.trim();
  const explicitIcon = header?.icon_type;  // Canonical field, not selectedIcon
  
  // Build search corpus from all available context
  const corpus = [
    funnelName || '',
    firstMessage || '',
    explicitTitle || ''
  ].join(' ').toLowerCase();
  
  // Find best matching pattern
  let bestMatch = null;
  let bestScore = 0;
  for (const pattern of CONTEXT_PATTERNS) {
    const score = pattern.keywords.filter(kw => corpus.includes(kw)).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = pattern;
    }
  }
  
  const resolved = bestMatch || CONTEXT_DEFAULT;
  
  return {
    headerTitle: explicitTitle || resolved.title,
    headerIcon: explicitIcon || resolved.icon,  // Use canonical icon_type
    invitation: resolved.invitation,
    cta: resolved.cta,
    category: 'engagement_entry',
    variant: 'invitation_compact'
  };
}

// ============================================================
// Conversational Acknowledgement Messages
// Rotated randomly to feel natural and avoid repetition
// ============================================================
// MA-283C-329: Response-aware acknowledgements
// Move conversational tone from question prefix into acknowledgement
const ACKNOWLEDGEMENTS_AFTER_YES = [
  "Absolutely.",
  "That makes sense.",
  "I hear you.",
];

const ACKNOWLEDGEMENTS_AFTER_NO = [
  "Totally understandable.",
  "That's completely fair.",
  "I appreciate your honesty.",
];

const ACKNOWLEDGEMENTS_DEFAULT = [
  "Got it.",
  "Thanks for sharing.",
  "Perfect.",
  "Great — thank you.",
  "Thanks for confirming.",
  "Noted.",
  "Sounds good.",
  "Okay, great.",
];

// ============================================================
// Variable Interpolation (Runtime)
// ============================================================

/**
 * Replace {node_id} and {field_name} references with collected values.
 * Builds a node_id → field_name map from the funnel nodes, then looks up
 * collected data by field name. Falls back to friendly label if no value yet.
 */
function interpolateRuntime(text, collectedData, funnelNodes) {
  if (!text || !text.includes('{')) return text;
  // Build lookup: node_id → field_name
  const nodeToField = {};
  const nodeLabels = {};
  (funnelNodes || []).forEach(n => {
    if (n.content?.field_name) nodeToField[n.id] = n.content.field_name;
    if (n.content?.input_type) nodeToField[n.id] = nodeToField[n.id] || n.content.input_type;
    nodeLabels[n.id] = n.label || n.content?.title || n.content?.input_type || n.id;
  });

  return text.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, ref) => {
    // Direct field name match in collected data
    if (collectedData?.[ref]) return collectedData[ref];
    // Node ID → field name → collected data
    const fieldName = nodeToField[ref];
    if (fieldName && collectedData?.[fieldName]) return collectedData[fieldName];
    // Fallback: friendly label (don't show raw {ref})
    const label = nodeLabels[ref];
    if (label) return label;
    return ref;
  });
}

// ============================================================
// MA-283C: Runtime Variant Resolution
// Resolves message content based on previous user answer
// ============================================================
const resolveMessageContent = (node, previousAnswer) => {
  // Check if node has message_variants (MA-283C)
  if (node?.content?.message_variants) {
    const variants = node.content.message_variants;
    
    // Select variant based on previous answer
    if (previousAnswer === "yes" && variants.after_yes?.content) {
      return variants.after_yes.content;
    }
    
    if (previousAnswer === "no" && variants.after_no?.content) {
      return variants.after_no.content;
    }
    
    // Default variant
    if (variants.default?.content) {
      return variants.default.content;
    }
  }
  
  // Fallback: Use legacy content field
  return node?.content?.message || node?.content?.title || '';
};

// ============================================================
// Get Previous Answer from Messages
// Extracts the last user response value (yes/no)
// MA-283C-312: Fully null-safe implementation
// ============================================================
const getPreviousAnswer = (messages) => {
  if (!messages || messages.length === 0) return null;
  
  // Find the last user message
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    
    if (msg.sender === 'user' || msg.type === 'user') {
      // PRIMARY: Check structured responseValue field
      if (msg.responseValue && typeof msg.responseValue === 'string') {
        const value = msg.responseValue.toLowerCase();
        if (value === 'yes' || value === 'no') {
          return value;
        }
      }
      
      // FALLBACK: Infer from text label (backward compatibility)
      if (msg.text && typeof msg.text === 'string') {
        const text = msg.text.toLowerCase();
        
        // Check if text starts with yes/no
        if (text.startsWith('yes')) return 'yes';
        if (text.startsWith('no')) return 'no';
      }
      
      // FALLBACK 2: Check message field
      if (msg.message && typeof msg.message === 'string') {
        const message = msg.message.toLowerCase();
        
        if (message.startsWith('yes')) return 'yes';
        if (message.startsWith('no')) return 'no';
      }
    }
  }
  
  return null;
};

// ============================================================
// Node-to-Step Converter
// Converts Session Engine node format to existing step format for UX compatibility
// This preserves all existing rendering logic without changes
// MA-283C: Adds message variant resolution
// ============================================================
const convertNodeToStep = (node, previousAnswer = null) => {
  // MA-295: ENTRY POINT TRACE
  console.log('[MA-295][CONVERT ENTRY] ========== convertNodeToStep CALLED ==========');
  console.log('[MA-295][CONVERT ENTRY] Received node:', node);
  console.log('[MA-295][CONVERT ENTRY] node.id:', node?.id);
  console.log('[MA-295][CONVERT ENTRY] node.node_type:', node?.node_type);
  console.log('[MA-295][CONVERT ENTRY] node.content:', node?.content);
  
  // MA-292: FUNCTION VERSION IDENTIFIER
  console.log('[MA-292][FUNCTION VERSION] convertNodeToStep executing - Version:', CONVERT_NODE_TO_STEP_VERSION);
  console.log('[MA-292][FUNCTION VERSION] This function includes MA-290 fix (responses || options)');
  
  // MA-293: Mark that conversion was called (will update UI fingerprint)
  window.MA293_CONVERT_CALLED = true;
  window.MA293_CONVERT_TIMESTAMP = Date.now();
  
  // MA-290: ACTIVE CONVERT ENTERED - This MUST appear if function is called
  console.log('[MA-290][ACTIVE CONVERT ENTERED] ========== FUNCTION START ==========');
  console.log('[MA-290][ACTIVE CONVERT ENTERED] node:', node);
  console.log('[MA-290][ACTIVE CONVERT ENTERED] node?.content?.responses:', node?.content?.responses);
  console.log('[MA-290][ACTIVE CONVERT ENTERED] node?.content?.options:', node?.content?.options);
  
  if (!node) {
    console.error('[MA-290][ACTIVE CONVERT] node is NULL - returning null');
    return null;
  }
  
  const content = node.content || {};
  const nodeType = node.node_type;
  
  // MA-290: Check for responses in multiple possible field names
  const rawResponses = content.responses || content.options || [];
  const hasResponses = rawResponses.length > 0;
  
  // MA-292: CRITICAL ASSERTION
  if (!rawResponses && (nodeType === 'question' || nodeType === 'response')) {
    console.error('[MA-292][CRITICAL] Question/response node has NO responses or options!');
    console.error('[MA-292][CRITICAL] content.responses:', content.responses);
    console.error('[MA-292][CRITICAL] content.options:', content.options);
    throw new Error('MA-292: Question node missing responses - conversion path broken');
  }
  
  // MA-283C: Resolve message content using variants if available
  const resolvedMessage = resolveMessageContent(node, previousAnswer);
  
  // MA-FE-077: Unified Response Step handling
  // - "response" type with responses → quick_reply
  // - "response" type without responses → message (auto-continue)
  // - Legacy "question" type → quick_reply
  // - Legacy "message" type → message
  const getStepType = () => {
    if (nodeType === 'response') {
      return hasResponses ? 'quick_reply' : 'message';
    }
    if (nodeType === 'question') {
      return 'quick_reply';
    }
    if (nodeType === 'message') {
      return 'message';
    }
    if (nodeType === 'input') {
      // MA-UI-108: Added zip_input as first-class input type for ZIP Intelligence
      // Added first_name_input and last_name_input for granular name collection
      return content.input_type === 'phone' ? 'phone_input' 
           : content.input_type === 'email' ? 'email_input'
           : content.input_type === 'name' ? 'name_input'
           : content.input_type === 'first_name' ? 'first_name_input'
           : content.input_type === 'last_name' ? 'last_name_input'
           : content.input_type === 'zip' ? 'zip_input'
           : 'text_input';
    }
    return nodeType;  // consent, booking, etc.
  };
  
  // MA-290: Map responses to options format (support both field names)
  const mappedOptions = hasResponses ? rawResponses.map(r => ({
    id: r.id,
    value: r.value || r.label,
    label: r.label
  })) : undefined;
  
  const step = {
    id: node.id,
    type: getStepType(),
    message: resolvedMessage,  // MA-283C: Use resolved message (with variants)
    field_name: content.field_name,
    placeholder: content.placeholder,
    input_color: content.input_color,  // Color for input submit button
    required: content.required !== false,
    // MA-290: Use mapped options (supports content.responses OR content.options)
    options: mappedOptions,
    
    // MA-PHONE-CONSENT-037: Consent-related fields (backward compatible)
    requires_consent: content.requires_consent,
    consent_text: content.consent_text,
    require_consent_checkbox: content.require_consent_checkbox !== false,
    submit_button_text: content.submit_button_text || (content.requires_consent ? "I Agree & Submit" : undefined),
    
    // Preserve node metadata for branching
    _node: node,
    _is_terminal: content.is_terminal,
    _outcome: content.outcome,
    _is_submit_step: content.is_submit_step  // MA-PHONE-CONSENT-037: For qualification
  };
  
  // MA-290: Validation logging
  console.log('[MA-290][CONVERT] Node type:', nodeType);
  console.log('[MA-290][CONVERT] Raw responses:', rawResponses);
  console.log('[MA-290][CONVERT] Has responses:', hasResponses);
  console.log('[MA-290][CONVERT] Mapped options:', mappedOptions);
  console.log('[MA-290][CONVERT] Final step.type:', step.type);
  console.log('[MA-290][CONVERT] Final step.options:', step.options);
  
  // MA-295: EXIT POINT TRACE
  console.log('[MA-295][CONVERT EXIT] ========== convertNodeToStep RETURNING ==========');
  console.log('[MA-295][CONVERT EXIT] Returning step:', step);
  console.log('[MA-295][CONVERT EXIT] step.type:', step?.type);
  console.log('[MA-295][CONVERT EXIT] step.message:', step?.message);
  console.log('[MA-295][CONVERT EXIT] step.options?.length:', step?.options?.length);
  console.log('[MA-295][CONVERT EXIT] Full step object:', JSON.stringify(step, null, 2));
  
  return step;
};

// ============================================================
// Global CSS for typing animation - injected once
// ============================================================
const TypingStyles = () => (
  <style>{`
    .typing-dot {
      width: 7px;
      height: 7px;
      min-width: 7px;
      min-height: 7px;
      flex-shrink: 0;
      background-color: #8E8E93;
      border-radius: 50%;
      display: block;
      animation: typingBounce 0.9s ease-in-out infinite;
    }
    .typing-dot-1 { animation-delay: 0s; }
    .typing-dot-2 { animation-delay: 0.2s; }
    .typing-dot-3 { animation-delay: 0.4s; }
    @keyframes typingBounce {
      0%, 100% { transform: translateY(0); opacity: 0.4; }
      50% { transform: translateY(-4px); opacity: 1; }
    }
  `}</style>
);

// ============================================================
// MA-ARCH-186-P0-FIX: LP Dark Theme Overrides
// When funnel is embedded inside a Landing Page, these styles
// transform it from white-card-on-dark to a native page element.
// Uses .lp-funnel-themed wrapper class for scoping.
// ============================================================
// MA-ARCH-183B: Token-driven theme style injection
// Both dark and light modes use the same CSS variable system
// Variables are resolved from brand tokens, not hardcoded

// Helper: generate a contrasting shade for the response area (matches FunnelCanvasPreview)
function getContrastingShade(hexColor) {
  if (!hexColor || hexColor.length < 7) return hexColor;
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Gamma-corrected luminance (exact match to FunnelCanvasPreview.getLuminance)
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map(c =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  const luminance = 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  const factor = luminance < 0.5 ? 1.4 : 0.85;
  const newR = Math.min(255, Math.max(0, Math.round(r * factor)));
  const newG = Math.min(255, Math.max(0, Math.round(g * factor)));
  const newB = Math.min(255, Math.max(0, Math.round(b * factor)));
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

// Helper: Generate typography CSS vars
function generateTypographyCSSVars(typo) {
  if (!typo) return '';
  const v = (name, val) => `--ma-typo-${name}: ${val};`;
  const scaleVar = (name, pair) => {
    if (!Array.isArray(pair)) return '';
    return `${v(`${name}-size`, pair[0])}\n      ${v(`${name}-lh`, pair[1])}`;
  };

  return `
      /* MA-TYPO-001: Typography Token Layer */
      ${v('font-primary', typo.fontPrimary || 'inherit')}
      ${v('font-secondary', typo.fontSecondary || 'inherit')}

      /* Scale */
      ${scaleVar('display-lg', typo.displayLarge)}
      ${scaleVar('display-md', typo.displayMedium)}
      ${scaleVar('heading-lg', typo.headingLarge)}
      ${scaleVar('heading-md', typo.headingMedium)}
      ${scaleVar('heading-sm', typo.headingSmall)}
      ${scaleVar('body-lg', typo.bodyLarge)}
      ${scaleVar('body-md', typo.bodyMedium)}
      ${scaleVar('body-sm', typo.bodySmall)}
      ${scaleVar('label', typo.label)}
      ${scaleVar('caption', typo.caption)}

      /* Weights */
      ${v('weight-regular', typo.weightRegular || 400)}
      ${v('weight-medium', typo.weightMedium || 500)}
      ${v('weight-semibold', typo.weightSemibold || 600)}
      ${v('weight-bold', typo.weightBold || 700)}
  `;
}

function generateTypographyMobileCSSVars(typo) {
  if (!typo) return '';
  const v = (name, val) => `--ma-typo-${name}: ${val};`;
  const scaleVar = (name, pair) => {
    if (!Array.isArray(pair)) return '';
    return `${v(`${name}-size`, pair[0])}\n        ${v(`${name}-lh`, pair[1])}`;
  };

  return `
    @media (max-width: 768px) {
      .ma-typo-root {
        ${scaleVar('display-lg', typo.displayLargeMobile)}
        ${scaleVar('display-md', typo.displayMediumMobile)}
        ${scaleVar('heading-lg', typo.headingLargeMobile)}
        ${scaleVar('heading-md', typo.headingMediumMobile)}
      }
    }
  `;
}

const ThemeStyleInjection = ({ tokens }) => {
  // MA-305 Phase 0: Light mode uses semantic responseBg token, dark mode uses legacy computation
  const responseAreaBg = tokens.surfaceMode === 'light' 
    ? (tokens.responseBg || tokens.containerBg)
    : tokens.containerBg;
  return (
  <style>{`
    .lp-funnel-themed {
      --lpf-surface: ${tokens.containerBg};
      --lpf-response-area-bg: ${responseAreaBg};
      --lpf-surface-2: ${tokens.surface2};
      --lpf-surface-3: ${tokens.surface1};
      --lpf-text: ${tokens.textPrimary};
      --lpf-text-muted: ${tokens.textMuted};
      --lpf-accent: ${tokens.accent};
      --lpf-accent-hover: ${tokens.accentStrong};
      --lpf-border: ${tokens.headerBorder};
      --lpf-bubble-agent: ${tokens.bubbleAgentBg};
      --lpf-bubble-agent-text: ${tokens.bubbleAgentText};
      --lpf-input-bg: ${tokens.inputBg};
      --lpf-input-text: ${tokens.inputText};
      --lpf-input-placeholder: ${tokens.inputPlaceholder};
      --lpf-typing-dot: ${tokens.typingDotColor};
      font-family: var(--ma-typo-font-secondary, ${tokens.fontBody});

      /* MA-TYPO-001: Typography token injection */
      ${generateTypographyCSSVars(tokens.typo)}
    }

    /* MA-TYPO-001: Responsive mobile overrides */
    ${generateTypographyMobileCSSVars(tokens.typo)}
    }

    @media (prefers-reduced-motion: reduce) {
      .lp-funnel-themed * {
        transition-duration: 0.01ms !important;
        animation-duration: 0.01ms !important;
      }
    }

    .lp-funnel-themed .lp-df-reply-chip:active:not(:disabled) {
      transform: scale(0.96) !important;
      box-shadow: 0 1px 3px ${tokens.accentGlow} !important;
    }

    .lp-funnel-themed .lp-df-agent-bubble {
      background: var(--lpf-bubble-agent) !important;
      border: 1px solid var(--lpf-border) !important;
      box-shadow: ${tokens.surfaceMode === 'dark' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'} !important;
    }
    .lp-funnel-themed .lp-df-agent-bubble p {
      color: var(--lpf-bubble-agent-text) !important;
    }

    .lp-funnel-themed .lp-df-typing-bubble {
      background: var(--lpf-bubble-agent) !important;
      border: 1px solid var(--lpf-border) !important;
      box-shadow: ${tokens.surfaceMode === 'dark' ? '0 1px 3px rgba(0,0,0,0.2)' : 'none'} !important;
    }
    .lp-funnel-themed .typing-dot {
      background-color: var(--lpf-typing-dot) !important;
    }

    .lp-funnel-themed .lp-df-user-bubble {
      background: var(--lpf-accent) !important;
    }

    .lp-funnel-themed .lp-df-reply-chip {
      background-color: var(--lpf-accent) !important;
      box-shadow: 0 2px 6px ${tokens.accentGlow}, 0 1px 2px rgba(0,0,0,0.1) !important;
    }
    .lp-funnel-themed .lp-df-reply-chip:hover:not(:disabled) {
      background-color: var(--lpf-accent-hover) !important;
      box-shadow: 0 4px 10px ${tokens.accentGlow}, 0 1px 3px rgba(0,0,0,0.12) !important;
    }

    .lp-funnel-themed .lp-df-text-input {
      background: var(--lpf-input-bg) !important;
      color: var(--lpf-input-text) !important;
      border: 1px solid var(--lpf-border) !important;
    }
    .lp-funnel-themed .lp-df-text-input::placeholder {
      color: var(--lpf-input-placeholder) !important;
    }
    .lp-funnel-themed .lp-df-text-input:focus {
      border-color: ${tokens.inputFocusBorder} !important;
      box-shadow: ${tokens.inputFocusGlow} !important;
    }

    .lp-funnel-themed .lp-df-send-active {
      background-color: var(--lpf-accent) !important;
      box-shadow: 0 2px 6px ${tokens.accentGlow} !important;
    }
    .lp-funnel-themed .lp-df-send-inactive {
      background-color: var(--lpf-surface-3) !important;
      color: var(--lpf-text-muted) !important;
    }

    .lp-funnel-themed .lp-df-response-area {
      /* MA-PPB-FUNNEL-SEAM-FIX: Changed from background to background-color to prevent shorthand override */
      /* Changed from !important to allow inline style precedence on first paint */
      background-color: var(--lpf-response-area-bg);
      border-color: var(--lpf-border);
    }

    .lp-funnel-themed .lp-df-skel-bar {
      background: ${tokens.skeletonBase} !important;
    }
    .lp-funnel-themed .lp-df-skel-area {
      background: var(--lpf-surface) !important;
      border-color: var(--lpf-border) !important;
    }

    .lp-funnel-themed .lp-df-error-title {
      color: #f87171 !important;
    }
    .lp-funnel-themed .lp-df-error-msg {
      color: var(--lpf-text-muted) !important;
    }

    .lp-funnel-themed .lp-df-consent-text {
      color: var(--lpf-text) !important;
    }

    .lp-funnel-themed .lp-df-validation-err {
      color: #f87171 !important;
    }

    .lp-funnel-themed .lp-df-scroll-btn {
      background: ${tokens.scrollIndicatorBg} !important;
      box-shadow: ${tokens.surfaceMode === 'dark' ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.12)'} !important;
    }
    .lp-funnel-themed .lp-df-scroll-arrow {
      stroke: var(--lpf-accent) !important;
    }
  `}</style>
  );
};

// ============================================================
// Typing Indicator - Pure CSS animation
// ============================================================
const TypingIndicator = () => (
  <div className="flex justify-start">
    <div 
      className={cn(
        "lp-df-typing-bubble flex items-center justify-center gap-[6px]",
        // MA-305: Use theme token instead of hardcoded color
        isDarkMode(lpSurfaceMode) ? "bg-white/10" : ""
      )}
      style={{
        backgroundColor: isDarkMode(lpSurfaceMode) ? undefined : tokens.bubbleAgentBg || tokens.surface_subtle || '#E9E9EB'
      }}
      style={{ 
        padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
        borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail}`,
        minWidth: '56px',
        height: `${DS.option.height}px`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
      }}
    >
      <span className="typing-dot typing-dot-1" />
      <span className="typing-dot typing-dot-2" />
      <span className="typing-dot typing-dot-3" />
    </div>
  </div>
);

// ============================================================
// Transitioning Agent Message - Shows typing dots OR message text
// ============================================================
const TransitioningAgentMessage = ({ message, isTyping }) => {
  return (
    <div className="flex justify-start">
      {isTyping ? (
        <div 
          className="lp-df-typing-bubble flex items-center justify-center gap-[6px]"
          style={{ 
            padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
            borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail}`,
            minWidth: '56px',
            height: `${DS.option.height}px`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
          }}
        >
          <span className="typing-dot typing-dot-1" />
          <span className="typing-dot typing-dot-2" />
          <span className="typing-dot typing-dot-3" />
        </div>
      ) : (
        <div 
          className="lp-df-agent-bubble"
          style={{ 
            maxWidth: DS.option.maxWidth,
            padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
            borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            minHeight: `${DS.option.height}px`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <p style={{ 
            fontSize: DS.text.option.size, 
            lineHeight: DS.text.option.lineHeight, 
            fontWeight: DS.text.option.weight,
            letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
            margin: 0
          }}>{message}</p>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Agent Message Bubble - Static, no animation
// ============================================================
const AgentMessage = ({ message }) => (
  <div className="flex justify-start">
    <div 
      className="lp-df-agent-bubble"
      style={{ 
        maxWidth: DS.option.maxWidth,
        padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
        borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        minHeight: `${DS.option.height}px`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <p style={{ 
        fontSize: DS.text.option.size, 
        lineHeight: DS.text.option.lineHeight, 
        fontWeight: DS.text.option.weight,
        letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
        margin: 0
      }}>{message}</p>
    </div>
  </div>
);

// ============================================================
// User Message Bubble - Static, no animation
// ============================================================
const UserMessage = ({ message, userColor = "#007AFF", userTextColor = "#FFFFFF" }) => (
  <div className="flex justify-end">
    <div 
      className="lp-df-user-bubble"
      style={{ 
        maxWidth: DS.option.maxWidth,
        padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
        borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail} ${DS.option.radius}`,
        backgroundColor: userColor,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        minHeight: `${DS.option.height}px`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <p style={{ 
        fontSize: DS.text.option.size, 
        lineHeight: DS.text.option.lineHeight,
        fontWeight: DS.text.option.weight,
        color: userTextColor,
        margin: 0
      }}>{message}</p>
    </div>
  </div>
);

// ============================================================
// Reply Chips — MA-ARCH-187 Redesign
// Controlled layout system with two modes:
//   Compact: short labels (age ranges, yes/no)
//   Standard: medium/long labels (full sentences)
// ============================================================
const ReplyChips = ({ options, onSelect, disabled, stepId, userColor = "#007AFF", textColor }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleSelect = (option) => {
    if (disabled || isTransitioning) return;
    setSelectedId(option.id);
    setIsTransitioning(true);
    setTimeout(() => onSelect(option), 180);
  };

  // Detect layout mode: compact if ALL labels are short (≤8 chars)
  const maxLabelLen = Math.max(...(options || []).map(o => (o.label || '').length));
  const isCompact = maxLabelLen <= 8;

  return (
    <div 
      className="flex flex-col items-end w-full"
      style={{ gap: `${DS.option.gap}px` }}
    >
      {options?.map((option, index) => {
        const isSelected = selectedId === option.id;
        const isUnselected = selectedId !== null && selectedId !== option.id;
        
        return (
          <button
            key={`${stepId}-${option.id}`}
            data-testid={`option-${option.id}`}
            onClick={() => handleSelect(option)}
            disabled={disabled || isTransitioning}
            className="lp-df-reply-chip touch-manipulation"
            style={{ 
              backgroundColor: userColor,
              color: textColor || '#ffffff',
              border: 'none',
              cursor: 'pointer',
              fontSize: DS.text.option.size,
              fontWeight: DS.text.option.weight,
              lineHeight: DS.text.option.lineHeight,
              padding: `${DS.option.paddingY}px ${DS.option.paddingX}px`,
              borderRadius: `${DS.option.radius} ${DS.option.radius} ${DS.option.radiusTail} ${DS.option.radius}`,
              maxWidth: DS.option.maxWidth,
              minWidth: isCompact ? `${DS.option.minWidthCompact}px` : `${DS.option.minWidthStandard}px`,
              minHeight: `${DS.option.height}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              // MA-TYPO-001: Single-line constraint — no multi-line wrapping
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              boxShadow: isSelected 
                ? '0 1px 2px rgba(0,0,0,0.1)' 
                : '0 2px 8px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.06)',
              opacity: isUnselected ? 0 : 1,
              transform: isSelected ? 'scale(0.97)' : 'scale(1)',
              transition: 'opacity 0.15s ease-out, transform 0.12s ease-out, box-shadow 0.12s ease-out',
              transitionDelay: isUnselected ? '0s' : `${index * 0.04}s`,
              letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

// ============================================================
// Text Input - with scroll position restoration
// Now accepts inputColor from step settings
// MA-UI-108: Added ZIP validation support
// MA-PHONE-CONSENT-037: Added consent UI support
// MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Added dynamic consent height measurement
// ============================================================
const TextInputStep = ({ step, onSubmit, disabled, prefillValue, inputColor = "#007AFF", buttonTextColor, tokens, onConsentHeightMeasured }) => {
  const [value, setValue] = useState(prefillValue || '');
  const [consentChecked, setConsentChecked] = useState(false);  // MA-PHONE-CONSENT-037
  const [validationError, setValidationError] = useState(null);
  const inputRef = useRef(null);
  const scrollPositionRef = useRef(null);
  const consentCardRef = useRef(null);  // MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Ref for measuring consent block
  
  // Use the inputColor from step, or fall back to default
  const buttonColor = step?.input_color || inputColor;
  // Use buttonTextColor if provided, otherwise use white as fallback
  const textColor = buttonTextColor || "#FFFFFF";
  
  // MA-UI-108: Check if this is a ZIP input type
  const isZipInput = step?.type === 'zip_input';
  
  // MA-PHONE-CONSENT-037: Consent requirement check
  const requiresConsent = step?.requires_consent === true;
  const consentText = step?.consent_text;
  const submitButtonText = step?.submit_button_text || "I Agree & Submit";
  
  // MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Measure consent block height dynamically
  useLayoutEffect(() => {
    if (requiresConsent && consentCardRef.current && onConsentHeightMeasured) {
      // Measure the full consent block including card + button + spacing
      const consentCard = consentCardRef.current;
      const consentHeight = consentCard.offsetHeight;
      
      // Report measured height to parent for layout calculations
      onConsentHeightMeasured(consentHeight);
    }
  }, [requiresConsent, consentText, onConsentHeightMeasured]);

  // Initialize with prefill value
  useEffect(() => {
    if (prefillValue && !value) {
      setValue(prefillValue);
    }
  }, [prefillValue]);
  
  // MA-UI-108: Clear validation error when value changes
  useEffect(() => {
    if (validationError && value) {
      setValidationError(null);
    }
  }, [value]);

  // NOTE: Removed auto-focus to prevent keyboard from opening automatically on mobile
  // User should tap the input field to open keyboard

  // Save scroll position when input is focused (before keyboard opens)
  const handleFocus = useCallback(() => {
    // Save both window scroll and parent window scroll (for iframes)
    scrollPositionRef.current = {
      window: window.scrollY || window.pageYOffset,
      parent: null
    };
    
    // Try to get parent scroll position if in iframe
    if (window.parent !== window) {
      try {
        window.parent.postMessage({
          type: 'MISSIONASSIST_SAVE_SCROLL_POSITION'
        }, '*');
      } catch (e) {
        // Ignore cross-origin errors
      }
    }
  }, []);
  
  // MA-UI-108: Handle ZIP input change with format enforcement
  const handleChange = useCallback((e) => {
    let newValue = e.target.value;
    
    // For ZIP inputs, format as user types
    if (isZipInput) {
      newValue = formatZipInput(newValue);
    }
    
    setValue(newValue);
  }, [isZipInput]);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    const trimmedValue = value.trim();
    
    if (!trimmedValue || disabled) return;
    
    // MA-UI-108: Validate ZIP format before submission
    if (isZipInput) {
      const validation = validateZipCode(trimmedValue);
      if (!validation.isValid) {
        setValidationError(validation.error);
        return;
      }
    }
    
    // MA-PHONE-CONSENT-037: Validate consent if required
    if (requiresConsent && !consentChecked) {
      setValidationError("Please accept the consent terms to continue");
      return;
    }
    
    // Blur input first to close keyboard
    inputRef.current?.blur();
    
    // Restore scroll position after keyboard closes
    setTimeout(() => {
      // Restore window scroll
      if (scrollPositionRef.current?.window !== null) {
        window.scrollTo({
          top: scrollPositionRef.current.window,
          behavior: 'smooth'
        });
      }
      
      // Tell parent to restore scroll (for iframes)
      if (window.parent !== window) {
        try {
          window.parent.postMessage({
            type: 'MISSIONASSIST_RESTORE_SCROLL_POSITION'
          }, '*');
        } catch (e) {
          // Ignore cross-origin errors
        }
      }
    }, 100);
    
    setValue('');
    setValidationError(null);
    
    // MA-PHONE-CONSENT-037: Include consent flag if applicable
    if (requiresConsent) {
      onSubmit(trimmedValue, { consent_given: consentChecked });
    } else {
      onSubmit(trimmedValue);
    }
  }, [value, disabled, onSubmit, isZipInput, requiresConsent, consentChecked]);

  const getInputType = () => {
    switch (step.type) {
      case 'phone_input': return 'tel';
      case 'email_input': return 'email';
      // MA-UI-108: ZIP uses text type but with numeric inputMode for mobile keyboard
      case 'zip_input': return 'text';
      default: return 'text';
    }
  };

  // MA-UI-108: Get inputMode for mobile keyboard optimization
  const getInputMode = () => {
    switch (step.type) {
      case 'phone_input': return 'tel';
      case 'email_input': return 'email';
      // MA-UI-108: ZIP code uses numeric keyboard on mobile for easier entry
      case 'zip_input': return 'numeric';
      default: return 'text';
    }
  };

  const getAutocomplete = () => {
    switch (step.field_name) {
      case 'first_name': return 'given-name';
      case 'last_name': return 'family-name';
      case 'phone': return 'tel';
      case 'email': return 'email';
      // MA-UI-108: Enable postal-code autocomplete for ZIP inputs
      case 'zip': return 'postal-code';
      case 'zip_code': return 'postal-code';
      default: return 'off';
    }
  };

  // MA-UI-108: Also check step type for autocomplete when field_name doesn't match
  const getAutocompleteFinal = () => {
    // First try field_name based autocomplete
    const fieldNameAutocomplete = getAutocomplete();
    if (fieldNameAutocomplete !== 'off') return fieldNameAutocomplete;
    
    // Fall back to step type
    if (step.type === 'zip_input') return 'postal-code';
    if (step.type === 'email_input') return 'email';
    if (step.type === 'phone_input') return 'tel';
    if (step.type === 'name_input') return 'name';
    if (step.type === 'first_name_input') return 'given-name';
    if (step.type === 'last_name_input') return 'family-name';
    
    return 'off';
  };

  const getPlaceholder = () => {
    if (step.placeholder) return step.placeholder;
    
    // Check step type first for consistent placeholder behavior
    if (step.type === 'phone_input') return '(555) 123-4567';
    if (step.type === 'email_input') return 'you@example.com';
    if (step.type === 'zip_input') return 'Enter ZIP code';
    if (step.type === 'name_input') return 'John Smith';
    if (step.type === 'first_name_input') return 'John';
    if (step.type === 'last_name_input') return 'Smith';
    
    // Fall back to field_name
    switch (step.field_name) {
      case 'first_name': return 'John';
      case 'last_name': return 'Smith';
      case 'phone': return '(555) 123-4567';
      case 'email': return 'you@example.com';
      case 'name': return 'John Smith';
      default: break;
    }
    
    return 'Type your response...';
  };

  // MA-FUNNEL-UI-072: Phone input validation - require minimum 10 digits
  const isPhoneInput = step.type === 'phone_input';
  const phoneDigitsOnly = isPhoneInput ? value.replace(/\D/g, '') : '';
  const hasMinPhoneDigits = isPhoneInput ? phoneDigitsOnly.length >= 10 : true;
  
  // Email validation - basic regex requiring local@domain.tld format
  // Prevents invalid emails like "1@1" from passing validation
  const isEmailInput = step.type === 'email_input';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const isValidEmail = isEmailInput ? emailRegex.test(value.trim()) : true;
  
  const isValid = value.trim().length > 0 && !disabled && hasMinPhoneDigits && isValidEmail;
  
  // MA-PHONE-CONSENT-037: Button enabled only when valid AND (consent checked if required)
  // MA-CONSENT-PLACEHOLDER-UPDATE-038: Also require consent text to be present
  // MA-FUNNEL-UI-072: For phone+consent, requires 10+ digits AND checkbox checked
  // For email+consent, requires valid email format AND checkbox checked
  const isSubmitEnabled = requiresConsent 
    ? (isValid && consentChecked && consentText && consentText.trim().length > 0) 
    : isValid;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col w-full" style={{ gap: `${DS.space.xs}px` }}>
      <div className="flex items-center w-full" style={{ gap: `${DS.space.sm}px`, borderBottom: 'none' }}>
        <input
          ref={inputRef}
          type={getInputType()}
          inputMode={getInputMode()}
          autoComplete={getAutocompleteFinal()}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          disabled={disabled}
          placeholder={getPlaceholder()}
          className={`lp-df-text-input flex-1 bg-[#F2F2F7] text-[#1C1C1E] placeholder:text-[#8E8E93] border-0 outline-none transition-all ${validationError ? 'ring-2 ring-red-400' : ''}`}
          data-testid="text-input"
          style={{ 
            WebkitAppearance: 'none',
            fontSize: DS.text.input.size,
            fontWeight: DS.text.input.weight,
            height: `${DS.input.height}px`,
            padding: `0 ${DS.space.lg}px`,
            borderRadius: DS.input.radius,
            letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
            '--tw-ring-color': validationError ? '#f87171' : buttonColor,
            borderBottom: 'none',  // MA-FUNNEL-UI-072: Remove divider
          }}
          maxLength={isZipInput ? 10 : undefined}
        />
        {/* Only show inline arrow if NO consent required */}
        {!requiresConsent && (
          <button
            type="submit"
            disabled={!isSubmitEnabled}
            className={`flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-150 ${isSubmitEnabled ? 'lp-df-send-active' : 'lp-df-send-inactive'}`}
            style={{
              width: `${DS.input.buttonSize}px`,
              height: `${DS.input.buttonSize}px`,
              backgroundColor: isSubmitEnabled ? buttonColor : '#E5E5EA',
              color: isSubmitEnabled ? textColor : '#8E8E93',
              borderRadius: '9999px',
              boxShadow: isSubmitEnabled ? '0 2px 6px rgba(0, 0, 0, 0.12)' : 'none'
            }}
            data-testid="send-button"
          >
            <ArrowUp className="w-5 h-5" strokeWidth={2.5} />
          </button>
        )}
      </div>
      
      {/* MA-PHONE-CONSENT-037: Consent Block (card + button) */}
      {/* MA-CONSENT-PLACEHOLDER-UPDATE-038: Show warning if consent text is empty */}
      {/* MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Wrapper ref for measuring total consent block height */}
      {requiresConsent && tokens && (
        <div ref={consentCardRef} className="flex flex-col" style={{ gap: `${DS.space.xs}px` }}>
          {/* Consent Card - MA-FUNNEL-UI-072: Removed top border to eliminate divider */}
          <div 
            className="consent-card"
            style={{
              background: consentText ? (tokens.inputBg || '#FFFFFF') : '#FEF2F2',
              borderLeft: consentText ? `1px solid ${tokens.borderSubtle || '#E5E7EB'}` : '1px solid #FCA5A5',
              borderRight: consentText ? `1px solid ${tokens.borderSubtle || '#E5E7EB'}` : '1px solid #FCA5A5',
              borderBottom: consentText ? `1px solid ${tokens.borderSubtle || '#E5E7EB'}` : '1px solid #FCA5A5',
              borderRadius: '12px',
              padding: '12px',
            }}
          >
            {consentText ? (
              <label className="flex gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => {
                    setConsentChecked(e.target.checked);
                    setValidationError(null);  // Clear error when checkbox changes
                  }}
                  className="sr-only"
                  data-testid="consent-checkbox"
                />
                <div 
                  style={{
                    width: '22px',
                    height: '22px',
                    minWidth: '22px',
                    borderRadius: '6px',
                    border: `2px solid ${tokens.accent || buttonColor}`,
                    background: consentChecked ? (tokens.accent || buttonColor) : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '2px',
                    transition: 'all 0.2s',
                  }}
                >
                  {consentChecked && (
                    <Check className="w-[14px] h-[14px]" strokeWidth={3} style={{ color: tokens.optionText || '#FFFFFF' }} />
                  )}
                </div>
                <div 
                  style={{ 
                    fontSize: '13px', 
                    color: tokens.textSecondary || '#6B7280',
                    lineHeight: '1.55',
                    flex: 1,
                  }}
                  dangerouslySetInnerHTML={{ __html: consentText }}
                />
              </label>
            ) : (
              // MA-CONSENT-PLACEHOLDER-UPDATE-038: Warning state when no consent text
              <div style={{
                fontSize: '13px',
                color: '#DC2626',
                lineHeight: '1.55',
                padding: '4px 0',
                fontWeight: 500,
              }}>
                ⚠️ Consent language required. Please add your TCPA consent language in the funnel builder.
              </div>
            )}
          </div>
          
          {/* Submit button */}
          <button
            type="submit"
            disabled={!isSubmitEnabled}
            className="lp-df-submit-btn"
            style={{
              width: '100%',
              height: '48px',
              borderRadius: '12px',
              background: tokens?.accent || buttonColor,
              color: tokens?.optionText || textColor,
              fontSize: '15px',
              fontWeight: 600,
              border: 'none',
              cursor: isSubmitEnabled ? 'pointer' : 'not-allowed',
              opacity: isSubmitEnabled ? 1 : 0.45,
              transition: 'opacity 0.2s, box-shadow 0.2s',
              boxShadow: isSubmitEnabled 
                ? `0 4px 10px ${tokens?.accentGlow || 'rgba(0, 122, 255, 0.2)'}` 
                : 'none',
            }}
            data-testid="consent-submit-button"
          >
            {submitButtonText}
          </button>
        </div>
      )}
      
      {/* MA-UI-108: Show validation error for ZIP inputs and consent */}
      {validationError && (
        <span className="lp-df-validation-err text-xs text-red-500 px-4" data-testid="validation-error">
          {validationError}
        </span>
      )}
    </form>
  );
};

// ============================================================
// MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Input Field Only Component
// Simplified input component without consent (for structured layout)
// ============================================================
const InputFieldOnly = ({ 
  step, 
  value, 
  onChange, 
  onKeyPress,
  disabled, 
  inputColor = "#007AFF", 
  tokens 
}) => {
  const inputRef = useRef(null);
  
  const buttonColor = step?.input_color || inputColor;
  const isZipInput = step?.type === 'zip_input';
  
  const getInputType = () => {
    if (step?.type === 'phone_input') return 'tel';
    if (step?.type === 'email_input') return 'email';
    if (isZipInput) return 'text';
    return 'text';
  };
  
  const getPlaceholder = () => {
    if (step?.placeholder) return step.placeholder;
    
    // Match realistic placeholders from other surfaces
    if (step?.type === 'phone_input') return '(555) 123-4567';
    if (step?.type === 'email_input') return 'you@example.com';
    if (step?.type === 'zip_input') return 'Enter ZIP code';
    if (step?.type === 'name_input') return 'John Smith';
    if (step?.type === 'first_name_input') return 'John';
    if (step?.type === 'last_name_input') return 'Smith';
    
    // Fall back to field_name
    if (step?.field_name === 'first_name') return 'John';
    if (step?.field_name === 'last_name') return 'Smith';
    if (step?.field_name === 'name') return 'John Smith';
    
    return 'Type your response...';
  };
  
  const getAutocomplete = () => {
    // Check step type first
    if (step?.type === 'email_input') return 'email';
    if (step?.type === 'phone_input') return 'tel';
    if (step?.type === 'name_input') return 'name';
    if (step?.type === 'first_name_input') return 'given-name';
    if (step?.type === 'last_name_input') return 'family-name';
    if (step?.type === 'zip_input') return 'postal-code';
    
    // Fall back to field_name
    if (step?.field_name === 'first_name') return 'given-name';
    if (step?.field_name === 'last_name') return 'family-name';
    if (step?.field_name === 'email') return 'email';
    if (step?.field_name === 'phone') return 'tel';
    if (step?.field_name === 'name') return 'name';
    
    return 'off';
  };
  
  return (
    <div className="flex items-center" style={{ gap: `${DS.space.xs}px` }}>
      <input
        ref={inputRef}
        type={getInputType()}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
        placeholder={getPlaceholder()}
        autoComplete={getAutocomplete()}
        disabled={disabled}
        data-testid="text-input"
        className="lp-df-text-input"
        style={{
          flex: 1,
          height: '48px',
          padding: '0 16px',
          fontSize: '15px',
          border: `1.5px solid ${tokens?.borderSubtle || '#E5E7EB'}`,
          borderRadius: '12px',
          background: tokens?.inputBg || '#FFFFFF',
          color: tokens?.text || '#111827',
          outline: 'none',
          transition: 'border-color 0.2s',
        }}
      />
    </div>
  );
};

// ============================================================
// MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Consent Block Component
// Extracted from TextInputStep for structural layout parity
// Handles consent checkbox, text display, and submit button
// MA-FUNNEL-UI-072: Added inputValue and stepType for phone validation
// ============================================================
const ConsentBlock = ({ 
  consentText, 
  submitButtonText, 
  onSubmit, 
  disabled, 
  buttonColor,
  buttonTextColor,
  tokens,
  inputValue = '',
  stepType = 'text_input'
}) => {
  const [consentChecked, setConsentChecked] = useState(false);
  const [validationError, setValidationError] = useState(null);
  
  const handleSubmit = () => {
    if (!consentChecked) {
      setValidationError("Please accept the consent terms to continue");
      return;
    }
    setValidationError(null);
    onSubmit({ consent_given: true });
  };
  
  // MA-FUNNEL-UI-072: Validate phone input has minimum 10 digits
  const isPhoneInput = stepType === 'phone_input';
  const phoneDigitsOnly = isPhoneInput ? inputValue.replace(/\D/g, '') : '';
  const hasMinPhoneDigits = isPhoneInput ? phoneDigitsOnly.length >= 10 : true;
  
  // Email validation - basic regex requiring local@domain.tld format
  const isEmailInput = stepType === 'email_input';
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const isValidEmail = isEmailInput ? emailRegex.test(inputValue.trim()) : true;
  
  const hasInputValue = inputValue && inputValue.trim().length > 0;
  
  const isSubmitEnabled = consentChecked && !disabled && hasInputValue && hasMinPhoneDigits && isValidEmail;
  
  return (
    <div className="flex flex-col" style={{ gap: `${DS.space.xs}px` }}>
      {/* Consent Card - MA-FUNNEL-UI-072: Theme-aware background */}
      <div 
        className="consent-card"
        style={{
          background: consentText 
            ? (tokens.surfaceMode === 'light' 
                ? (tokens.inputBg || '#FFFFFF') 
                : 'rgba(255, 255, 255, 0.08)')
            : (tokens.surfaceMode === 'light' ? '#FEF2F2' : 'rgba(254, 226, 226, 0.1)'),
          border: consentText 
            ? `1px solid ${tokens.borderSubtle || (tokens.surfaceMode === 'light' ? '#E5E7EB' : 'rgba(255,255,255,0.1)')}` 
            : `1px solid ${tokens.surfaceMode === 'light' ? '#FCA5A5' : 'rgba(254, 202, 202, 0.3)'}`,
          borderRadius: '12px',
          padding: '12px',
        }}
      >
        {consentText ? (
          <label className="flex gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => {
                setConsentChecked(e.target.checked);
                setValidationError(null);
              }}
              className="sr-only"
              data-testid="consent-checkbox"
            />
            <div 
              style={{
                width: '22px',
                height: '22px',
                minWidth: '22px',
                borderRadius: '6px',
                border: `2px solid ${tokens.accent || buttonColor}`,
                background: consentChecked ? (tokens.accent || buttonColor) : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '2px',
                transition: 'all 0.2s',
              }}
            >
              {consentChecked && (
                <Check className="w-[14px] h-[14px]" strokeWidth={3} style={{ color: tokens.optionText || '#FFFFFF' }} />
              )}
            </div>
            <div 
              style={{ 
                fontSize: '13px', 
                color: tokens.surfaceMode === 'light' 
                  ? (tokens.text || '#1F2937')
                  : (tokens.botText || '#E5E7EB'),  // Light text for dark mode
                lineHeight: '1.55',
                flex: 1,
              }}
              dangerouslySetInnerHTML={{ __html: consentText }}
            />
          </label>
        ) : (
          <div style={{
            fontSize: '13px',
            color: '#DC2626',
            lineHeight: '1.55',
            padding: '4px 0',
            fontWeight: 500,
          }}>
            ⚠️ Consent language required. Please add your TCPA consent language in the funnel builder.
          </div>
        )}
      </div>
      
      {/* Validation Error */}
      {validationError && (
        <div 
          style={{
            fontSize: '13px',
            color: '#DC2626',
            padding: '4px 0',
          }}
        >
          {validationError}
        </div>
      )}
      
      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!isSubmitEnabled}
        className="lp-df-submit-btn"
        style={{
          width: '100%',
          height: '48px',
          marginTop: '12px',  // Extra spacing above button
          borderRadius: '12px',
          background: tokens?.accent || buttonColor,
          color: tokens?.optionText || buttonTextColor,
          fontSize: '15px',
          fontWeight: 600,
          border: 'none',
          cursor: isSubmitEnabled ? 'pointer' : 'not-allowed',
          opacity: isSubmitEnabled ? 1 : 0.45,
          transition: 'opacity 0.2s, box-shadow 0.2s',
          boxShadow: isSubmitEnabled 
            ? `0 4px 10px ${tokens?.accentGlow || 'rgba(0, 122, 255, 0.2)'}` 
            : 'none',
        }}
        data-testid="consent-submit-button"
      >
        {submitButtonText || "I Agree & Submit"}
      </button>
    </div>
  );
};

// ============================================================
// Consent Step
// ============================================================
// ============================================================
// Consent Step - Uses content from funnel builder
// Displays title, consent text (with HTML), and custom button
// Checkbox positioned at top-left of consent box (no separate label)
// Uses accentColor from appearance for checkbox and button
// ============================================================
const ConsentStep = ({ onAccept, disabled, content = {}, accentColor, tokens = {} }) => {
  const [agreed, setAgreed] = useState(false);
  
  // MA-284: STEP 1 - Log raw tokens
  console.log('[MA-284][ConsentStep] tokens:', tokens);
  console.log('[MA-284][ConsentStep] bubbleAgentText:', tokens.bubbleAgentText);
  console.log('[MA-284][ConsentStep] textPrimary:', tokens.textPrimary);
  console.log('[MA-284][ConsentStep] textSecondary:', tokens.textSecondary);
  
  // MA-THEME-PHASE7: Token-derived colors — no hardcoded hex values
  const cardBg = tokens.containerBg || tokens.cardBg || 'transparent';
  const cardBorder = tokens.cardBorder || `1px solid ${tokens.borderSubtle || 'rgba(255,255,255,0.08)'}`;
  // Extract color from borderSubtle (it's full CSS like "1px solid #e2e8f0")
  const borderSubtleValue = tokens.borderSubtle;
  const dividerColor = borderSubtleValue && borderSubtleValue.includes('solid') 
    ? borderSubtleValue.split('solid')[1].trim() 
    : (borderSubtleValue || 'rgba(255,255,255,0.06)');
  const titleColor = tokens.textPrimary || 'inherit';
  // MA-284-PPB-FUNNEL-CONSENT-TEXT-PARITY-331: Use bubbleAgentText to match FB Canvas semantic alignment
  // Consent text is bot-delivered content, not secondary UI text
  const bodyColor = tokens.bubbleAgentText || tokens.textPrimary || 'inherit';
  
  // MA-284: STEP 2 - Log final color
  console.log('[MA-284][ConsentStep] bodyColor:', bodyColor);
  
  const disabledBg = tokens.borderSubtle || 'rgba(255,255,255,0.08)';
  const disabledText = tokens.textMuted || 'inherit';
  const errorColor = tokens.error || '#ef4444';
  const checkboxUncheckedBg = tokens.inputBg || 'transparent';
  const accent = accentColor || tokens.accent || '#6366F1';
  
  // Extract content from funnel builder or use defaults
  const title = content?.title || "Consent Step";
  const consentText = content?.consent_text || "";
  const buttonText = content?.button_text || "I Agree & Continue";
  const requireCheckbox = content?.require_checkbox !== false;

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ background: cardBg, border: cardBorder, borderRadius: '12px', overflow: 'hidden' }}>
        {/* Title section */}
        <div style={{ padding: '20px 16px 12px', borderBottom: `1px solid ${dividerColor}` }}>
          <h3 style={{ fontSize: '18px', fontWeight: 700, color: titleColor, margin: 0 }}>
            {title}
          </h3>
        </div>
        
        {/* Consent text section with checkbox */}
        <div style={{ padding: '12px 16px' }}>
          <div className="flex gap-3">
            {requireCheckbox && (
              <label className="flex-shrink-0 cursor-pointer mt-[2px]">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="sr-only"
                  data-testid="consent-checkbox"
                />
                <div 
                  style={{
                    width: '22px', height: '22px', borderRadius: '6px',
                    border: `2px solid ${accent}`,
                    backgroundColor: agreed ? accent : checkboxUncheckedBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background-color 0.15s, border-color 0.15s',
                  }}
                >
                  {agreed && <Check className="w-[14px] h-[14px]" strokeWidth={3} style={{ color: tokens.optionText || '#ffffff' }} />}
                </div>
              </label>
            )}
            <div 
              style={{ fontSize: '13px', color: bodyColor, lineHeight: '1.55' }}
              className="consent-text-content"
            >
              {consentText ? (
                <span dangerouslySetInnerHTML={{ __html: consentText }} />
              ) : (
                <span style={{ color: tokens.textMuted || '#9ca3af', fontStyle: 'italic' }}>
                  Consent language not configured
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Submit button section */}
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${dividerColor}` }}>
          {requireCheckbox && !agreed && (
            <p style={{ fontSize: '12px', color: errorColor, textAlign: 'center', marginBottom: '8px' }} data-testid="consent-error-message">
              Please accept the terms and conditions by checking the box above
            </p>
          )}
          <button
            onClick={() => (requireCheckbox ? agreed : true) && !disabled && onAccept()}
            disabled={(requireCheckbox && !agreed) || disabled}
            style={(requireCheckbox ? agreed : true) ? {
              width: '100%', height: '44px', borderRadius: '12px', fontWeight: 600, fontSize: '15px',
              background: accent, color: tokens.optionText || 'white', border: 'none', cursor: 'pointer',
              boxShadow: `0 4px 10px ${accent}40`, transition: 'all 0.15s', opacity: 1,
            } : {
              width: '100%', height: '44px', borderRadius: '12px', fontWeight: 600, fontSize: '15px',
              background: accent, color: tokens.optionText || 'white', border: 'none', cursor: 'not-allowed',
              transition: 'all 0.15s', opacity: 0.45,
            }}
            data-testid="consent-submit"
          >
            {disabled ? 'Processing...' : buttonText}
          </button>
        </div>
      </div>
      
      {/* Style for links in consent text */}
      <style>{`
        .consent-text-content a {
          color: #2563eb;
          text-decoration: underline;
        }
        .consent-text-content a:hover {
          color: #1d4ed8;
        }
      `}</style>
    </div>
  );
};

// ============================================================
// Booking Step - Transparent wrapper, widget handles its own styling
// MA-UI-097: Pass display settings to widget
// ============================================================
const BookingStep = ({ bookingConfig, contactInfo, funnelContext, onSuccess, onError, onNextStep, content, tokens = {}, accentColor = null }) => {
  // MA-UI-097: Extract display settings from content.config
  // MA-UI-100: Add durationMinutes from manual config
  const config = content?.config || bookingConfig || {};
  const headerSettings = config.header_settings || {};
  
  // Heading priority: content > config.header_settings > defaults
  // MA-FUNNEL-ARCH-083: Prioritize content fields (set in Canvas) over header_settings (legacy)
  const heading = content?.heading || headerSettings.heading || content?.title;
  const subheading = content?.subheading || headerSettings.subheading || content?.subtitle;
  
  const showTimezone = config.show_timezone !== false;
  const showDuration = config.show_duration !== false;
  const durationMinutes = config.duration_minutes;  // MA-UI-100: Manual duration
  const showCalendarTitle = config.show_calendar_title === true;
  const calendarTitle = config.calendar_name;
  
  // MA-LP-BOOKING-TITLE-FIX: Extract appointment title from config ONLY
  // DO NOT use heading as fallback - heading is for UI display, not GHL event title
  // If no explicit appointment_title is set, pass undefined so GHL uses its own configured event name
  const appointmentTitle = config.appointment_title || config.title || undefined;
  
  return (
    <div 
      className="booking-step-wrapper" 
      data-testid="booking-section"
      style={{ 
        background: 'transparent',
        padding: 0,
      }}
    >
      {/* Step header - Only show if no custom heading is set */}
      {!heading && (
        <div 
          className="text-center mb-4 px-4"
          style={{ maxWidth: '760px', margin: '0 auto 16px auto' }}
        >
          <p 
            className="text-xs font-semibold uppercase tracking-wider mb-1"
            style={{ 
              color: tokens.textMuted || '#64748B',
            }}
          >
            Step 2
          </p>
          <h2 
            className="text-lg font-semibold"
            style={{ 
              color: tokens.textPrimary || '#1E293B',
            }}
          >
            Choose a Time for Your Review
          </h2>
        </div>
      )}
      
      {/* Calendar widget - renders its own premium card */}
      <QuickBookingWidget
        contact={contactInfo}
        calendarId={config.calendar_id || bookingConfig?.calendar_id}
        locationId={config.location_id || bookingConfig?.location_id}
        appointmentTitle={appointmentTitle}
        heading={heading}
        subheading={subheading}
        showTimezone={showTimezone}
        showDuration={showDuration}
        durationMinutes={durationMinutes}
        showCalendarTitle={showCalendarTitle}
        calendarTitle={calendarTitle}
        funnelContext={funnelContext}
        onBookingSuccess={onSuccess}
        onBookingError={onError}
        onNextStep={onNextStep}
        embedded={true}
        tokens={tokens}
        accentColor={accentColor || tokens.primary}
      />
    </div>
  );
};

// ============================================================
// Header Component - Now accepts appearance settings
// Supports both selectedIcon (built-in icons) and customIconUrl (uploaded)
// MA-PPB-CONSENT-HEADER-078: Uses shared icon resolver
// ============================================================
const FunnelHeader = ({ appearance, isLpDark = false, resolvedContext = null, tokens }) => {
  const contextTitle = resolvedContext?.headerTitle;
  const contextIcon = resolvedContext?.headerIcon;
  
  const rawTitle = contextTitle || appearance?.title || "";
  const customIconUrl = appearance?.customIconUrl;
  
  // MA-PPB-CONSENT-HEADER-078: Build header object for shared resolver
  const headerForResolver = {
    icon_type: contextIcon || appearance?.selectedIcon || appearance?.icon_type
  };
  
  const title = rawTitle;
  const IconComponent = resolveHeaderIcon(headerForResolver);

  return (
    <div
      data-testid="funnel-header"
      className="flex items-center px-4"
      style={{
        height: `${LAYOUT.HEADER_HEIGHT}px`,
        backgroundColor: tokens.headerBg,
        // MA-305: Light mode uses soft divider (no shadow), dark mode unchanged
        borderBottom: tokens.surfaceMode === 'light' 
          ? '1px solid rgba(0, 0, 0, 0.06)' 
          : `1px solid ${tokens.headerBorder}`,
        boxShadow: tokens.surfaceMode === 'dark' ? 'none' : (tokens.surfaceMode === 'light' ? 'none' : 'inset 0 -1px 2px rgba(0,0,0,0.03)'),
        flexShrink: 0
      }}
    >
      <div className="flex items-center gap-2.5">
        {customIconUrl ? (
          <img src={customIconUrl} alt="Icon" className="w-8 h-8 rounded-lg object-contain" style={{ backgroundColor: tokens.headerIconBg }} />
        ) : IconComponent ? (
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: tokens.accent || tokens.headerIconBg }}
          >
            <IconComponent className="w-4 h-4" style={{ color: tokens.headerIconColor === tokens.accent ? (tokens.optionText || '#000000') : (tokens.headerIconColor || '#ffffff') }} />
          </div>
        ) : title?.trim() ? (
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: tokens.headerIconBg }}
          >
            <span className="font-bold text-[14px] text-white">
              {title.charAt(0)}
            </span>
          </div>
        ) : null}
        {title && (
          <span style={{ color: tokens.headerTitleColor, letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)', fontSize: 'var(--ma-typo-body-lg-size, 15px)', fontWeight: 'var(--ma-typo-weight-semibold, 600)', lineHeight: 'var(--ma-typo-body-lg-lh, 24px)' }}>
            {title}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Main Component
// ============================================================
// MA-292: BUILD IDENTIFIER
const CONVERSATION_FUNNEL_BUILD_ID = 'MA-292-v2024-04-24-19-50-UTC';
const CONVERT_NODE_TO_STEP_VERSION = 'v2-MA290-MA292';

// MA-PHASE11A-FINAL-CONSOLE-CLEANUP-023: Build logs removed for production

export default function ConversationFunnel({
  tenantId,
  funnelSlug,
  embedKey,                 // MA-TENANT-001: Preferred secure embed identifier
  onComplete,
  onBookingStart,
  onStepViewed,
  onStepCompleted,
  onConsentGiven,
  onBookingSuccess,
  onError,
  onFunnelLoaded,           // MA-OPS-061: Callback when funnel data is loaded
  trustedFormRuntime = null, // MA-OPS-061: TrustedForm runtime integration
  attribution = {},
  prefillData = {},
  externalTrustedFormCertUrl = null,
  className = '',
  testMode = false,  // When true, no records are created
  lpTheme = null,    // MA-ARCH-186-P0-FIX: 'lp_dark' or 'lp_light' when embedded in LP
  lpTokens = null,   // MA-LP-LAYOUT-027: LP accent tokens for theme inheritance
  brandConfig = null,  // MA-ARCH-183B: Brand config for token resolution
  consentTextOverride = null  // MA-LP-CONSENT-ENRICH: Override consent text for LP preview
}) {
  // MA-ARCH-187B: Derived theme flags — MUST be declared before any hooks/effects
  // MA-LP-LAYOUT-027B: Support both lp_dark and lp_light
  const isLpThemed = lpTheme?.startsWith('lp_');
  const isLpDark = lpTheme === 'lp_dark';
  const lpSurfaceMode = isLpDark ? 'dark' : (lpTheme === 'lp_light' ? 'light' : null);
  
  // MA-THEME-WIDGET-MIGRATION-012: Canonical theme system
  // Funnel theme resolved via canonical theme engine
  const [funnelTheme, setFunnelTheme] = useState(null);
  // MA-287: Store builder theme lpTokens for centralized theme resolution (must be declared before tokens useMemo)
  const [resolvedLpTokensFromBuilder, setResolvedLpTokensFromBuilder] = useState(null);
  
  // MA-298: Store appearance from session for canonical token resolution
  // Ref avoids React hoisting bug (appearance state declared later at line 2134)
  // Enables Canvas parity: ConversationFunnel passes same appearance context as FunnelCanvasPreview
  const sessionAppearanceRef = useRef(null);
  
  // MA-289: Debug panel state tracking
  const [debugState, setDebugState] = useState({
    builderTheme: null,
    builderThemeValid: false,
    themeSource: 'unknown',
    renderBlocker: null
  });


  const tokens = useMemo(() => {
    // MA-284: STEP 3 - Log theme source inputs
    console.log('[MA-284][TokenBuilder] lpTokens:', lpTokens);
    console.log('[MA-284][TokenBuilder] isLpThemed:', isLpThemed);
    console.log('[MA-284][TokenBuilder] lpSurfaceMode:', lpSurfaceMode);
    
    // MA-287: Priority 1 - Use builder theme from session (treat as lpTokens for full expansion)
    const effectiveLpTokens = resolvedLpTokensFromBuilder || lpTokens;
    const effectiveIsLpThemed = !!resolvedLpTokensFromBuilder || isLpThemed;
    
    // MA-298: Get canonical tokens for parity-critical surfaces
    // Pass session appearance to match Canvas token resolution (FunnelCanvasPreview.jsx)
    // Only applies to CASE 1 (builder theme / LP embedding)
    const canonicalTokens = effectiveLpTokens 
      ? resolveThemeTokens(effectiveLpTokens, sessionAppearanceRef.current)
      : null;
    
    // MA-298: Debug canonical token availability
    console.log('[MA-298 DEBUG]', {
      hasCanonicalTokens: !!canonicalTokens,
      canonicalBotBubbleBg: canonicalTokens?.botBubbleBg,
      fallbackCardBg: effectiveLpTokens?.cardBg,
      willUse: canonicalTokens?.botBubbleBg ?? effectiveLpTokens?.cardBg
    });
    
    // MA-298: Log canonical token resolution for Canvas comparison
    if (canonicalTokens) {
      console.log('[MA-298 FB TEST PAGE CHECK]', {
        containerColor: canonicalTokens.containerColor,
        threadColor: canonicalTokens.threadColor,
        botBubbleBg: canonicalTokens.botBubbleBg,
        userColor: canonicalTokens.userColor,
        source: 'ConversationFunnel_resolveThemeTokens',
        lpTokens_surface1: effectiveLpTokens?.surface1,
        lpTokens_cardBg: effectiveLpTokens?.cardBg,
      });
      console.log('[MA-298 CANONICAL]', {
        input_surface1: effectiveLpTokens?.surface1,
        input_cardBg: effectiveLpTokens?.cardBg,
        output_threadColor: canonicalTokens.threadColor,
        output_botBubbleBg: canonicalTokens.botBubbleBg,
        output_containerColor: canonicalTokens.containerColor
      });
    }
    
    if (resolvedLpTokensFromBuilder) {
      console.log('[MA-287][Theme Priority] Using builder theme from session');
      console.log('[MA-287][TOKENS FINAL] resolvedLpTokensFromBuilder:', resolvedLpTokensFromBuilder);
      console.log('[MA-287][TOKENS FINAL KEYS]', Object.keys(resolvedLpTokensFromBuilder || {}));
    }
    
    // CASE 1: LP Embedding or Builder Theme - lpTokens already come from canonical LP bridge or session
    if (effectiveLpTokens?.accent && effectiveIsLpThemed) {
      // MA-298: Guard - require canonicalTokens for parity-critical tokens
      if (!canonicalTokens) {
        console.warn('[MA-298] Waiting for canonicalTokens...');
        // MA-305: Use theme tokens even in loading state instead of hardcoded fallbacks
        const tempTheme = resolveTheme({ preset: 'professional', mode: effectiveLpTokens.surfaceMode || 'light' });
        const tempTokens = tempTheme.tokens;
        return {
          containerBg: tempTokens.surface_elevated || '#ffffff',
          threadBg: tempTokens.surface_base || '#ffffff',
          bubbleAgentBg: tempTokens.surface_elevated || '#ffffff',  // MA-305 Phase 0: Parity baseline (white, matches Canvas)
          bubbleAgentText: tempTokens.text_primary || '#000000',
          accent: effectiveLpTokens.accent,
          textPrimary: tempTokens.text_primary || '#000000',
          textSecondary: tempTokens.text_secondary || '#666666',
          borderSubtle: tempTokens.border_subtle || '#e5e7eb',
          // Minimal set for loading state
          accentRgb: '139,92,246',
          surfaceMode: effectiveLpTokens.surfaceMode || 'light'
        };
      }
      
      const isDark = resolvedLpTokensFromBuilder ? (effectiveLpTokens.surfaceMode === 'dark') : (lpSurfaceMode === 'dark');
      const [r, g, b] = hexToRgb(effectiveLpTokens.accent);
      const accentRgb = `${r},${g},${b}`;
      
      return {
        // MA-298: PARITY-CRITICAL TOKENS (canonical only, NO fallback)
        containerBg: canonicalTokens.containerColor,
        threadBg: canonicalTokens.threadColor,
        bubbleAgentBg: canonicalTokens.botBubbleBg,  // MA-305: Use calibrated bot bubble
        bubbleAgentText: canonicalTokens.botBubbleText,
        accent: canonicalTokens.userColor,
        textPrimary: canonicalTokens.botBubbleText,
        textSecondary: canonicalTokens.textMuted,
        borderSubtle: canonicalTokens.borderColor,
        
        // MA-305: Semantic surface tokens (Phase 0 parity)
        headerBg: canonicalTokens.headerBg || canonicalTokens.containerColor,
        responseBg: canonicalTokens.responseBg || canonicalTokens.containerColor,
        
        // EXISTING TOKENS (unchanged)
        // Surfaces
        pageBg: effectiveLpTokens.bgPage,
        surface1: effectiveLpTokens.surface1,
        surface2: effectiveLpTokens.surface1,
        cardBg: effectiveLpTokens.cardBg,
        elevatedBg: effectiveLpTokens.surface1,
        // Text (from LP bridge)
        textMuted: effectiveLpTokens.textMuted,
        textInverse: effectiveLpTokens.textOnAccent,
        // Header
        headerBorder: effectiveLpTokens.borderSubtle,
        headerTitleColor: effectiveLpTokens.textPrimary,
        headerIconBg: effectiveLpTokens.accentSoft,
        headerIconColor: effectiveLpTokens.accent,
        // Bubbles
        bubbleUserBg: effectiveLpTokens.accentSoft,
        bubbleUserText: effectiveLpTokens.textOnAccentSoft,
        // Accent / Options
        accentRgb,
        accentStrong: effectiveLpTokens.accentStrong,
        accentGlow: `rgba(${accentRgb},0.15)`,
        optionBg: effectiveLpTokens.accent,
        optionText: effectiveLpTokens.textOnAccent,
        optionHoverOpacity: '0.9',
        // Input
        inputBg: isDark ? 'rgba(255,255,255,0.06)' : '#F2F2F7',
        inputText: effectiveLpTokens.textPrimary,
        inputPlaceholder: effectiveLpTokens.textMuted,
        inputBorder: effectiveLpTokens.borderSubtle,
        inputFocusBorder: effectiveLpTokens.accent,
        inputFocusGlow: `0 0 0 3px rgba(${accentRgb},0.15)`,
        // Card styling
        cardBorder: effectiveLpTokens.cardBorder || effectiveLpTokens.borderSubtle,
        cardShadowPrimary: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.06)',
        borderSubtle: effectiveLpTokens.borderSubtle,
        // Chrome
        scrollIndicatorBg: isDark ? 'rgba(255,255,255,0.12)' : effectiveLpTokens.cardBg,
        scrollIndicatorBorder: effectiveLpTokens.borderSubtle,
        scrollArrowColor: effectiveLpTokens.accent,
        gradientFadeRgb: isDark ? '17,24,39' : '255,255,255',
        error: '#ef4444',
        typingDotColor: effectiveLpTokens.textMuted,
        compactMessageBg: effectiveLpTokens.cardBg || effectiveLpTokens.surface1,  // MA-305: Use LP tokens instead of hardcoded fallback
        compactMessageText: effectiveLpTokens.textPrimary,
        skeletonBase: effectiveLpTokens.cardBg || effectiveLpTokens.surface1,  // MA-305: Use LP tokens instead of hardcoded fallback
        surfaceMode: isDark ? 'dark' : 'light',
        // Layout
        fontBody: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",
        radiusLg: '16px',
      };
    }
    // CASE 2 & 3: Use funnel theme from backend or resolve with canonical engine
    const theme = funnelTheme || resolveTheme({ preset: 'professional', mode: isLpDark ? 'dark' : 'light' });
    const t = theme.tokens;
    
    // MA-284: STEP 3 - Log theme tokens
    console.log('[MA-284][TokenBuilder] theme t:', t);
    
    const themeIsDark = theme.mode === 'dark';
    const [r, g, b] = hexToRgb(t.primary);
    const accentRgb = `${r},${g},${b}`;
    
    return {
      // Surfaces (canonical tokens)
      pageBg: t.surface_base,
      surface1: t.surface_subtle,
      surface2: t.surface_subtle,
      containerBg: t.surface_elevated,
      cardBg: t.surface_elevated,
      threadBg: t.surface_base,
      elevatedBg: t.surface_elevated,
      // Text (canonical tokens)
      textPrimary: t.text_primary,
      textSecondary: t.text_secondary,
      textMuted: t.text_secondary,
      textInverse: t.text_inverse,
      // Header
      headerBg: t.surface_header || t.surface_elevated,  // MA-305 Phase 0: Semantic header token
      headerBorder: t.border_subtle,
      headerTitleColor: t.text_primary,
      headerIconBg: t.accent_soft,
      headerIconColor: t.primary,
      // MA-305: Response area semantic token
      responseBg: t.surface_elevated,
      // Bubbles
      bubbleAgentBg: t.surface_elevated,  // MA-305 Phase 0: Parity baseline (white, matches Canvas)
      bubbleAgentText: t.text_primary,
      bubbleUserBg: t.accent_soft,
      bubbleUserText: t.text_on_accent_soft,
      // Accent / Options
      accent: t.primary,
      accentStrong: t.primary_hover,
      accentGlow: t.highlight,
      optionBg: t.primary,
      optionText: t.primary_foreground,
      optionHoverOpacity: '0.9',
      // Input
      inputBg: t.surface_subtle,
      inputText: t.text_primary,
      inputPlaceholder: t.text_secondary,
      inputBorder: t.border_subtle,
      inputFocusBorder: t.primary,
      inputFocusGlow: t.highlight,
      // Card styling
      cardBorder: `1px solid ${t.border_subtle}`,
      cardShadowPrimary: themeIsDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.06)',
      borderSubtle: t.border_subtle,
      // Chrome
      scrollIndicatorBg: t.surface_elevated,
      scrollIndicatorBorder: `1px solid ${t.border_subtle}`,
      scrollArrowColor: t.primary,
      gradientFadeRgb: themeIsDark ? '15,23,42' : '255,255,255',
      error: t.error,
      typingDotColor: t.text_secondary,
      compactMessageBg: t.surface_subtle,  // MA-305: Use canonical token instead of hardcoded fallback
      compactMessageText: t.text_primary,
      skeletonBase: t.surface_subtle,
      surfaceMode: theme.mode,
      // Layout
      fontBody: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif",
      radiusLg: '16px',
    };
  }, [funnelTheme, isLpDark, isLpThemed, lpSurfaceMode, lpTokens, resolvedLpTokensFromBuilder]);

  // ===== MA-298 RUNTIME VALIDATION (UNCONDITIONAL) =====
  console.log('%c[MA-298 VALIDATION START]', 'background: #00ff00; color: #000; font-weight: bold; padding: 4px;');
  console.log('[MA-298 TOKENS FINAL]', {
    threadBg: tokens.threadBg,
    bubbleAgentBg: tokens.bubbleAgentBg,
    containerBg: tokens.containerBg,
    match: tokens.threadBg === tokens.bubbleAgentBg ? '❌ COLLISION' : '✅ DISTINCT'
  });
  console.log('[MA-298 APPEARANCE REF]', {
    hasRef: !!sessionAppearanceRef.current,
    appearance_threadColor: sessionAppearanceRef.current?.threadColor,
    appearance_containerColor: sessionAppearanceRef.current?.containerColor,
    appearance_userColor: sessionAppearanceRef.current?.userColor
  });
  console.log('[MA-298 LP TOKENS]', {
    hasLpTokens: !!lpTokens,
    hasResolvedBuilder: !!resolvedLpTokensFromBuilder,
    surface1: lpTokens?.surface1 || resolvedLpTokensFromBuilder?.surface1,
    cardBg: lpTokens?.cardBg || resolvedLpTokensFromBuilder?.cardBg,
    bgPage: lpTokens?.bgPage || resolvedLpTokensFromBuilder?.bgPage
  });
  console.log('%c[MA-298 VALIDATION END]', 'background: #ff0000; color: #fff; font-weight: bold; padding: 4px;');
  
  // ===== MA-299 HEADER BACKGROUND CONSISTENCY VALIDATION =====
  // MA-305 Phase 0: Response area uses semantic token (parity baseline)
  const responseAreaBgComputed = tokens.containerBg;  // Direct token, no computation
  console.log('%c[MA-299 VALIDATION]', 'background: #ffff00; color: #000; font-weight: bold; padding: 4px;', {
    containerBg: tokens.containerBg,
    headerBg: tokens.headerBg,
    responseAreaBg: responseAreaBgComputed,
    match: tokens.headerBg === responseAreaBgComputed ? '✅ EXACT MATCH' : '❌ MISMATCH'
  });

  // MA-284: STEP 3 - Log final resolved tokens
  console.log('[MA-284][TokenBuilder] resolved tokens:', tokens);
  
  // MA-289: USER RUNTIME LOGGING - Final tokens
  console.log('[MA-289][USER RUNTIME] ========== FINAL TOKENS ==========');
  console.log('[MA-289][USER RUNTIME] final tokens:', tokens);
  console.log('[MA-289][USER RUNTIME] accent:', tokens?.accent);
  console.log('[MA-289][USER RUNTIME] containerBg:', tokens?.containerBg);
  console.log('[MA-289][USER RUNTIME] cardBg:', tokens?.cardBg);

  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  
  // MA-287: RENDER GUARD - Trace render state (MUST be after useState declarations)
  console.log('[MA-287][RENDER] Component render state:', {
    loading,
    error,
    sessionId,
    resolvedLpTokensFromBuilder,
    tokens_keys: tokens ? Object.keys(tokens).length : 0
  });
  const [funnelId, setFunnelId] = useState(null);  // MA-ARCH-082: Funnel ID for booking events
  const [retryCount, setRetryCount] = useState(0); // MA-LP-URL-026: Retry mechanism for transient failures
  
  // Appearance settings from funnel config
  // MA-ARCH-183B: Defaults now derived from brand tokens
  const [appearance, setAppearance] = useState(() => ({
    title: "",
    titleIcon: null,
    selectedIcon: null,
    customIconUrl: null,
    prospectColor: tokens.accent,
    userColor: tokens.accent,
    userTextColor: tokens.bubbleUserText,
    systemColor: tokens.bubbleAgentBg,
    containerColor: tokens.containerBg,
    threadColor: tokens.threadBg,
  }));
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [currentStepId, setCurrentStepId] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stepStartTime, setStepStartTime] = useState(Date.now());
  
  // MA-293: VISIBLE UI FINGERPRINT (top of render)
  const [executionFingerprint, setExecutionFingerprint] = useState({
    path: 'NOT_SET',
    convertCalled: 'NO',
    optionsCount: 0
  });

  // MA-287: RENDER GUARD - Additional state tracking (after state declarations)
  console.log('[MA-287][RENDER STATE]', {
    currentStep,
    currentStepId,
    messages_count: messages?.length,
    loading,
    error
  });
  
  // MA-288: NODE STATE DIAGNOSTICS (nodes array doesn't exist - uses currentStep instead)
  console.log('[MA-288][STATE]', {
    currentStep,
    currentStepType: currentStep?.type,
    showOptions
  });
  console.log('[MA-288][CURRENT STEP FULL]', currentStep);
  
  // MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Dynamic consent block height
  // Measured from actual rendered consent card + button + spacing
  // Default 240px is initial estimate, updated by TextInputStep measurement
  const [consentBlockHeight, setConsentBlockHeight] = useState(240);
  
  // MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Input value for structured consent layout
  const [structuredInputValue, setStructuredInputValue] = useState('');
  
  // Transitioning message state - for smooth typing → message animation
  // { id, message, isTyping } - message shows typing dots when isTyping=true
  const [transitioningMessage, setTransitioningMessage] = useState(null);
  
  // Layout mode: 'compact' (initial) | 'expanded' (after first response)
  const [layoutMode, setLayoutMode] = useState('compact');
  
  // MA-ARCH-187B-REFINEMENT: Compact entry state
  // When false, renders the actual conversation thread + response chips directly in compact mode
  // The first question appears in the thread and response options in the response area
  const [compactEntryActive, setCompactEntryActive] = useState(false);
  const [resolvedContext, setResolvedContext] = useState(null);
  
  // Funnel state: 'chat' | 'consent' | 'booking'
  const [funnelState, setFunnelState] = useState('chat');
  const [bookingConfig, setBookingConfig] = useState(null);
  const [bookingContent, setBookingContent] = useState(null);  // MA-UI-097: Full booking node content
  const [bookingNodeId, setBookingNodeId] = useState(null);  // MA-ARCH-082: Booking node ID for events
  const [consentContent, setConsentContent] = useState(null);  // Full consent node content
  const [leadId, setLeadId] = useState(null);
  const [journeyId, setJourneyId] = useState(null);  // MA-FUNNEL-LEAD-001: Journey ID for dispatch
  // Initialize collected responses with prefill data
  const [collectedResponses, setCollectedResponses] = useState(() => {
    const initial = {};
    if (prefillData.first_name) initial.first_name = prefillData.first_name;
    if (prefillData.last_name) initial.last_name = prefillData.last_name;
    if (prefillData.email) initial.email = prefillData.email;
    if (prefillData.phone) initial.phone = prefillData.phone;
    return initial;
  });
  
  // TrustedForm - use external cert if provided, otherwise initialize internally
  const [trustedFormUrl, setTrustedFormUrl] = useState(externalTrustedFormCertUrl);

  // Update trustedFormUrl if external prop changes
  useEffect(() => {
    if (externalTrustedFormCertUrl) {
      setTrustedFormUrl(externalTrustedFormCertUrl);
    }
  }, [externalTrustedFormCertUrl]);


  // MA-284: STEP 4 - Verify renderer instance
  console.log('[MA-284] ConversationFunnel RENDERED', { embedKey, lpTheme, testMode });

  // MA-277B: LP Step Debug Instrumentation (dev/preview only)
  // Outputs resolved themeEngine tokens for visual model comparison
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugTheme = urlParams.get('debugTheme') === '1';
    
    if (debugTheme && funnelId) {
      console.log('[FUNNEL_THEME_DEBUG]', {
        surface: 'PPB_LP_Step',
        funnelId: funnelId,
        stylingMode: 'themeEngine',
        surfaceMode: tokens?.surfaceMode || 'unknown',
        
        appearance: {
          containerColor: appearance?.containerColor,
          threadColor: appearance?.threadColor,
          headerBackgroundColor: appearance?.headerBackgroundColor,
          userColor: appearance?.userColor,
          botTextColor: appearance?.botTextColor,
          userTextColor: appearance?.userTextColor,
          iconBackgroundColor: appearance?.iconBackgroundColor,
        },
        
        lpTokens: lpTokens ? {
          bgPage: lpTokens.bgPage,
          cardBg: lpTokens.cardBg,
          accent: lpTokens.accent,
          textPrimary: lpTokens.textPrimary,
          textOnAccent: lpTokens.textOnAccent,
          borderSubtle: lpTokens.borderSubtle,
        } : null,
        
        themeEngineTokens: {
          threadBg: tokens?.threadBg,
          containerBg: tokens?.containerBg,
          bubbleAgentBg: tokens?.bubbleAgentBg,
          bubbleAgentText: tokens?.bubbleAgentText,
          accent: tokens?.accent,
          optionText: tokens?.optionText,
          headerBg: tokens?.headerBg,
          borderSubtle: tokens?.borderSubtle,
          textPrimary: tokens?.textPrimary,
        },
        
        resolved: {
          threadColor: tokens?.threadBg,
          botBubbleBg: tokens?.bubbleAgentBg,
          userColor: tokens?.accent,
          headerBg: tokens?.headerBg,
          borderColor: tokens?.borderSubtle,
        },
        
        sources: {
          threadColor: appearance?.threadColor ? 'appearance.threadColor' : 'lpTokens.bgPage → themeEngine',
          botBubbleBg: appearance?.containerColor ? 'appearance.containerColor' : 'themeEngine.bubbleAgentBg',
          userColor: appearance?.userColor ? 'appearance.userColor' : 'themeEngine.accent',
          headerBg: appearance?.containerColor ? 'appearance.containerColor' : 'themeEngine.headerBg',
          borderColor: 'themeEngine.borderSubtle',
        },
        
        visualModel: {
          separation: 'fill-contrast (themeEngine canonical)',
          threadBgValue: tokens?.threadBg,
          botBubbleValue: tokens?.bubbleAgentBg,
          contrast: tokens?.threadBg === tokens?.bubbleAgentBg ? 'COLLISION' : 'distinct',
        }
      });
    }
  }, [funnelId, appearance, lpTokens, tokens]);
  
  // Refs
  const messagesEndRef = useRef(null);
  const threadRef = useRef(null);
  const containerRef = useRef(null);
  const responseAreaRef = useRef(null);
  
  // Scroll indicator state - tracks both directions independently
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [showScrollUp, setShowScrollUp] = useState(false);

  // ============================================================
  // Check if response area needs scroll indicators (up/down)
  // ============================================================
  const checkScrollIndicators = useCallback(() => {
    if (!responseAreaRef.current) return;
    
    const el = responseAreaRef.current;
    const hasOverflow = el.scrollHeight > el.clientHeight + 5;
    
    if (!hasOverflow) {
      setShowScrollDown(false);
      setShowScrollUp(false);
      return;
    }
    
    const scrollTop = el.scrollTop;
    const scrollBottom = el.scrollHeight - el.clientHeight - scrollTop;
    
    // Show down arrow when not at bottom (more than 10px from bottom)
    setShowScrollDown(scrollBottom > 10);
    // Show up arrow when scrolled down (more than 10px from top)
    setShowScrollUp(scrollTop > 10);
  }, []);
  
  // Hide scroll indicators when submitting
  const hideScrollIndicators = useCallback(() => {
    setShowScrollDown(false);
    setShowScrollUp(false);
  }, []);
  
  // Scroll to top/bottom handlers
  const scrollResponseToBottom = useCallback(() => {
    if (responseAreaRef.current) {
      responseAreaRef.current.scrollTo({
        top: responseAreaRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);
  
  const scrollResponseToTop = useCallback(() => {
    if (responseAreaRef.current) {
      responseAreaRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  // Check scroll indicators when options change or step changes
  useEffect(() => {
    // Reset and check after content renders
    const checkOverflow = () => {
      if (!responseAreaRef.current) {
        return;
      }
      const el = responseAreaRef.current;
      const hasOverflow = el.scrollHeight > el.clientHeight + 5;
      
      if (!hasOverflow) {
        setShowScrollDown(false);
        setShowScrollUp(false);
        return;
      }
      
      const scrollTop = el.scrollTop;
      const scrollBottom = el.scrollHeight - el.clientHeight - scrollTop;
      
      // Initially at top: show down arrow only if overflow
      setShowScrollDown(scrollBottom > 10);
      setShowScrollUp(scrollTop > 10);
    };
    
    setShowScrollDown(false);
    setShowScrollUp(false);
    const timer1 = setTimeout(checkOverflow, 200);
    const timer2 = setTimeout(checkOverflow, 500);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [currentStep, showOptions, compactEntryActive, layoutMode]);

  // ============================================================
  // Smooth scroll to bottom of thread (only after user interaction)
  // ============================================================
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  const scrollToBottom = useCallback(() => {
    // Scroll thread to show latest message
    if (threadRef.current) {
      threadRef.current.scrollTo({
        top: threadRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll thread when messages change (always scroll to show new messages)
  // MA-FUNNEL-UI-072: Exclude consent-enabled inputs from auto-scroll to keep question visible
  useEffect(() => {
    const isConsentStep = currentStep?.requires_consent && currentStep?.type?.includes('input');
    if (funnelState === 'chat' && layoutMode === 'expanded' && !isConsentStep) {
      // Delay to allow message to render
      const timer = setTimeout(scrollToBottom, 150);
      return () => clearTimeout(timer);
    }
  }, [messages, transitioningMessage, scrollToBottom, funnelState, layoutMode, currentStep?.id, currentStep?.requires_consent]);

  // MA-FUNNEL-UI-072: Scroll to bottom when consent step first loads
  // Use useLayoutEffect to run synchronously before browser paint, preventing visible jump
  useLayoutEffect(() => {
    const isConsentStep = currentStep?.requires_consent && currentStep?.type?.includes('input');
    if (isConsentStep && layoutMode === 'expanded' && threadRef.current) {
      // Scroll immediately, synchronously with DOM update
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [currentStep?.id, layoutMode]);

  // ============================================================
  // Prevent horizontal shift on keyboard (critical for mobile)
  // ============================================================
  useLayoutEffect(() => {
    // Store original body styles
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    
    // Prevent body scroll and position changes
    const lockLayout = () => {
      document.body.style.overflowX = 'hidden';
      document.documentElement.style.overflowX = 'hidden';
    };
    
    lockLayout();
    
    // Handle visual viewport changes (keyboard)
    const handleViewportChange = () => {
      if (containerRef.current) {
        // Force recenter without transforms
        containerRef.current.style.marginLeft = 'auto';
        containerRef.current.style.marginRight = 'auto';
      }
    };
    
    // Listen to visual viewport if available (mobile keyboards)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    }
    
    window.addEventListener('resize', handleViewportChange);
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      }
      window.removeEventListener('resize', handleViewportChange);
    };
  }, []);

  // ============================================================
  // TrustedForm initialization
  // MA-EMBED-001: TrustedForm is now managed by useTrustedFormRuntime hook
  // ConversationFunnel receives the cert URL via props from parent component
  // DO NOT load TrustedForm script here - it's handled by the hook
  // ============================================================
  useEffect(() => {
    if (externalTrustedFormCertUrl) {
      console.log('[ConversationFunnel] Using TrustedForm cert from runtime:', externalTrustedFormCertUrl);
      setTrustedFormUrl(externalTrustedFormCertUrl);
    }
  }, [externalTrustedFormCertUrl]);

  // ============================================================
  // Start session - Uses new Session Engine API
  // Creates session via POST /api/funnel-sessions
  // Locks to published funnel version (backend is source of truth)
  // ============================================================
  const initCalledRef = useRef(false);
  useEffect(() => {
    // Guard against double-execution (React StrictMode)
    if (initCalledRef.current) return;
    initCalledRef.current = true;
    
    const init = async () => {
      console.log('[MA-291][INIT] ========== FUNNEL INITIALIZATION START ==========');
      console.log('[MA-291][INIT] embedKey:', embedKey);
      console.log('[MA-291][INIT] funnelSlug:', funnelSlug);
      console.log('[MA-291][INIT] testMode:', testMode);
      
      // MA-292: ROUTE AND MODE LOGGING
      console.log('[MA-292][ROUTE] window.location.href:', window.location.href);
      console.log('[MA-292][MODE] testMode:', testMode, 'embedKey:', embedKey, 'funnelSlug:', funnelSlug);
      console.log('[MA-292][EXPECTED] Should see [MA-290][ACTIVE CONVERT ENTERED] and [MA-290][CONVERT] logs');
      
      try {
        // MA-TENANT-001: Use embed_key for storage key when available
        const storageKey = `funnel_session_${embedKey || funnelSlug}`;
        const storedSessionId = localStorage.getItem(storageKey);
        
        // FIX: In test mode, always start fresh — clear cached session
        if (testMode && storedSessionId) {
          localStorage.removeItem(storageKey);
        }
        
        if (storedSessionId && !testMode) {
          console.log('[MA-291][PATH A] Attempting to resume existing session:', storedSessionId);
          // Try to resume existing session
          try {
            const resumeResponse = await fetch(`${API_URL}/api/funnel-sessions/${storedSessionId}`);
            if (resumeResponse.ok) {
              const sessionData = await resumeResponse.json();
              
              // Check if session is resumable
              if (sessionData.is_resumable && sessionData.status === 'active') {
                console.log('[MA-291][PATH A1] Session is resumable - restoring state');
                console.log('[ConversationFunnel] Resuming session:', storedSessionId);
                
                setSessionId(sessionData.session_id);
                // MA-FUNNEL-LEAD-001: Store journey_id and lead_id from session
                if (sessionData.journey_id) setJourneyId(sessionData.journey_id);
                if (sessionData.lead_id) setLeadId(sessionData.lead_id);
                setLoading(false);
                
                // MA-253: Apply canonical header from resumed session
                if (sessionData.header) {
                  console.log('[MA-253] Applying canonical header from resumed session:', sessionData.header);
                  setAppearance(prev => ({
                    ...prev,
                    title: sessionData.header.title || prev.title,
                    selectedIcon: sessionData.header.icon_type || prev.selectedIcon,
                  }));
                }
                
                // Apply appearance settings from funnel config
                if (sessionData.appearance) {
                  setAppearance(prev => ({ ...prev, ...sessionData.appearance }));
                }
                // MA-THEME-WIDGET-MIGRATION-012: Resolve theme via canonical engine
                // Backend may send brand_appearance (from themeToFunnelAppearance)
                // If not, resolve theme from appearance accent color
                if (!brandConfig && !lpTokens) {
                  const ap = sessionData.appearance || {};
                  const anchorColor = ap.userColor || ap.prospectColor || '#2563EB';
                  const mode = isLpDark ? 'dark' : 'light';
                  const theme = resolveTheme({ preset: 'professional', anchor_color: anchorColor, mode });
                  setFunnelTheme(theme);
                } else if (brandConfig && !lpTokens) {
                  // Resolve from brandConfig (brand default_theme)
                  const theme = resolveTheme({ 
                    preset: brandConfig.preset || 'professional',
                    anchor_color: brandConfig.anchor_color || brandConfig.accent || '#2563EB',
                    mode: brandConfig.mode || (isLpDark ? 'dark' : 'light')
                  });
                  setFunnelTheme(theme);
                }
                
                // Restore state from session
                const currentNode = sessionData.current_node;
                if (currentNode) {
                  console.log('[MA-291][PATH A2] Converting resumed node to step');
                  // MA-283C: Get previous answer for variant resolution
                  const previousAnswer = getPreviousAnswer(messages);
                  
                  // Convert node to step format for existing UX
                  const step = convertNodeToStep(currentNode, previousAnswer);
                  
                  // Restore messages from node path (simplified - just show current message)
                  setMessages([{ 
                    type: 'agent', 
                    message: currentNode.content?.message || '', 
                    id: `msg-${Date.now()}` 
                  }]);
                  setCurrentStep(step);
                  setCurrentStepId(currentNode.id);
                  
                  // Restore collected data
                  if (sessionData.collected_data) {
                    setCollectedResponses(sessionData.collected_data);
                  }
                  
                  // Check current node type for special handling
                  if (currentNode.node_type === 'consent') {
                    // Resume on consent step - set consent content and state
                    setConsentContent(currentNode.content);
                    setFunnelState('consent');
                    setLoading(false);
                    return;
                  }
                  
                  // If consent was given, check if we should go to booking
                  if (sessionData.consent_given && !currentNode.content?.is_terminal) {
                    // Session had consent, check current node type
                    if (currentNode.node_type === 'booking') {
                      setBookingContent(currentNode.content);
                      setBookingConfig({
                        calendar_id: currentNode.content?.config?.calendar_id,
                        location_id: currentNode.content?.config?.location_id,
                      });
                      setBookingNodeId(currentNode.id);
                      setFunnelState('booking');
                      setLoading(false);
                      return;
                    }
                  }
                  
                  setTimeout(() => setShowOptions(true), 50);
                  setLayoutMode('expanded'); // Resume in expanded mode
                  
                  onStepViewed?.({
                    stepId: currentNode.id,
                    stepType: step.type,
                    sessionId: sessionData.session_id
                  });
                }
                setStepStartTime(Date.now());
                return;
              }
            }
            // Session not resumable or not found - clear and create new
            localStorage.removeItem(`funnel_session_${embedKey || funnelSlug}`);
          } catch (resumeErr) {
            console.log('[ConversationFunnel] Could not resume session, creating new');
            localStorage.removeItem(`funnel_session_${embedKey || funnelSlug}`);
          }
        }
        
        // MA-TENANT-001: Create new session via Session Engine
        // Prefer embed_key for tenant-safe resolution, fallback to funnel_slug
        console.log('[MA-291][PATH B] Creating NEW session');
        const sessionBody = {
          test_mode: testMode,  // When true, session won't create lead records
          // MA-CAMPAIGNS-003: Pass campaign_id at top level for explicit attribution
          campaign_id: attribution?.campaign_id || null,
          attribution: attribution ? {
            utm_source: attribution.utm_source,
            utm_medium: attribution.utm_medium,
            utm_campaign: attribution.utm_campaign,
            utm_adset: attribution.utm_adset,
            utm_ad: attribution.utm_ad,
            utm_content: attribution.utm_content,
            utm_term: attribution.utm_term,
            referrer: attribution.referrer || document.referrer,
            landing_page: window.location.href,
            // MA-CAMPAIGNS-003: Include campaign_id in attribution for storage
            campaign_id: attribution.campaign_id,
            // Click IDs
            fbclid: attribution.fbclid,
            gclid: attribution.gclid
          } : {
            referrer: document.referrer,
            landing_page: window.location.href
          }
        };
        
        // Use embed_key if available (preferred), otherwise fall back to funnel_slug
        if (embedKey) {
          sessionBody.embed_key = embedKey;
        } else if (funnelSlug) {
          sessionBody.funnel_slug = funnelSlug;
        }
        
        const createResponse = await fetch(`${API_URL}/api/funnel-sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sessionBody)
        });

        if (!createResponse.ok) {
          const errorData = await createResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to start session');
        }
        
        const data = await createResponse.json();
        
        // MA-297: Local flag to prevent duplicate conversion (React state is async)
        let initialStepAssigned = false;
        
        // MA-297: FORCED CONVERSION — REQUIRED UNTIL CANONICAL PATH PROVEN
        console.log('[MA-297][FORCED] Immediate conversion before any logic');
        
        if (!data.current_node) {
          console.error('[MA-297][CRITICAL] No current_node in response');
          throw new Error('Session missing current_node');
        }
        
        // FORCE CONVERSION NOW
        const forcedStep = convertNodeToStep(data.current_node);
        console.log('[MA-297][FORCED RESULT]', forcedStep);
        
        if (!forcedStep) {
          console.error('[MA-297][CRITICAL] Conversion failed despite valid node');
          throw new Error('convertNodeToStep returned null');
        }
        
        // FORCE STATE ASSIGNMENT
        setCurrentStep(forcedStep);
        setCurrentStepId(data.current_node.id);
        initialStepAssigned = true; // Mark as assigned (local flag, not state)
        
        console.log('[MA-297][FORCED STATE SET]', {
          type: forcedStep.type,
          message: !!forcedStep.message,
          options: forcedStep.options?.length,
          initialStepAssigned: initialStepAssigned
        });
        
        // Show message and options together
        setMessages([{ 
          type: 'agent', 
          message: data.current_node.content?.message || '', 
          id: `msg-${Date.now()}` 
        }]);
        
        // Show options immediately (minimal delay for React render)
        setTimeout(() => {
          setShowOptions(true);
        }, 50);
        
        // MA-295: RAW SESSION PAYLOAD (BEFORE ANY PROCESSING)
        console.log('[MA-295][SESSION PAYLOAD RAW]', data);
        console.log('[MA-295][VALIDATION] current_node present?', !!data?.current_node);
        console.log('[MA-295][VALIDATION] nodes present?', !!data?.nodes);
        console.log('[MA-295][VALIDATION] nodes count:', data?.nodes?.length || 0);
        console.log('[MA-295][VALIDATION] version_source:', data?.version_source || 'NOT PROVIDED');
        console.log('[MA-295][VALIDATION] current_node.content.responses:', data?.current_node?.content?.responses);
        console.log('[MA-295][VALIDATION] current_node.content.options:', data?.current_node?.content?.options);
        console.log('[MA-295][VERSION CHECK]', {
          hasDraft: !!data?.version?.draft,
          hasPublished: !!data?.version?.published,
          versionSource: data?.version_source,
          nodesFrom: data?.nodes?.length ? 'nodes' : 'unknown',
        });
        console.log('[MA-295][ENTRY NODE CHECK]', {
          current_node_id: data?.current_node?.id,
          current_node_type: data?.current_node?.node_type,
        });
        
        // MA-289: USER RUNTIME LOGGING
        console.log('[MA-289][USER RUNTIME] ========== SESSION RESPONSE ==========');
        console.log('[MA-289][USER RUNTIME] session response:', data);
        console.log('[MA-289][USER RUNTIME] builder_theme:', data?.builder_theme);
        console.log('[MA-289][USER RUNTIME] session_id:', data?.session_id);
        console.log('[MA-289][USER RUNTIME] funnel_id:', data?.funnel_id);
        console.log('[MA-289][USER RUNTIME] current_node:', data?.current_node);
        
        // MA-290: SESSION STRUCTURE AUDIT
        console.log('[MA-290][SESSION RAW] Full data object:', data);
        console.log('[MA-290][SESSION RAW] Has current_node:', !!data?.current_node);
        console.log('[MA-290][SESSION RAW] current_node.node_type:', data?.current_node?.node_type);
        console.log('[MA-290][SESSION RAW] current_node.content:', data?.current_node?.content);
        console.log('[MA-290][SESSION RAW] current_node.content.message:', data?.current_node?.content?.message);
        console.log('[MA-290][SESSION RAW] current_node.content.responses:', data?.current_node?.content?.responses);
        console.log('[MA-290][SESSION RAW] current_node.content.options:', data?.current_node?.content?.options);
        
        // Store session ID for potential resume (reuse storageKey from above)
        localStorage.setItem(storageKey, data.session_id);
        
        setSessionId(data.session_id);
        setFunnelId(data.funnel_id);  // MA-ARCH-082: Store funnel ID for booking events
        // MA-FUNNEL-LEAD-001: Store journey_id from new session
        if (data.journey_id) setJourneyId(data.journey_id);
        
        // MA-253: Read canonical header from session response
        if (data.header) {
          console.log('[MA-253] Applying canonical header:', data.header);
          setAppearance(prev => ({
            ...prev,
            title: data.header.title || prev.title,
            selectedIcon: data.header.icon_type || prev.selectedIcon,
          }));
        }
        
        // MA-287: Extract builder_theme for centralized theme resolution (MUST run before MA-186)
        console.log('[MA-287][ACTIVE CALLBACK] session creation success callback fired');
        console.log('[MA-287][SESSION RAW] data:', data);
        console.log('[MA-287][SESSION BUILDER_THEME]', data?.builder_theme);
        
        // MA-304 Phase 1: Use ONLY finalTheme if available
        if (data.finalTheme) {
          console.log('[MA-304][Phase1] finalTheme found in session:', {
            theme_id: data.finalTheme.theme_id,
            source: data.finalTheme.source,
            mode: data.finalTheme.mode
          });
          
          // Use pre-computed lpTokens from finalTheme
          const lpTokensFromFinalTheme = data.finalTheme.computed?.lpTokens;
          
          if (lpTokensFromFinalTheme) {
            console.log('[MA-304][Phase1] Using finalTheme.computed.lpTokens:', lpTokensFromFinalTheme);
            
            // Apply finalTheme appearance directly
            setAppearance(prev => ({
              ...prev,
              ...data.finalTheme.computed.funnelAppearance
            }));
            
            // Store for theme resolution
            setResolvedLpTokensFromBuilder(lpTokensFromFinalTheme);
            setLoading(false);
            
            // Call onFunnelLoaded callback
            if (onFunnelLoaded) {
              onFunnelLoaded({
                funnelId: data.funnel_id,
                funnelName: data.funnel_name,
                settings: data.funnel_settings,
                appearance: data.finalTheme.computed.funnelAppearance,
                finalTheme: data.finalTheme,
              });
            }
            
            console.log('[MA-304][Phase1] ✅ Theme applied from finalTheme - no fallback needed');
            // SKIP all old fallback logic below
          } else {
            console.warn('[MA-304][Phase1] finalTheme.computed.lpTokens missing - falling back to old logic');
          }
        }
        
        // OLD FALLBACK LOGIC (MA-304 Phase 1: Only runs if finalTheme missing)
        // TODO: Remove in Phase 3 after all surfaces migrated
        if (!data.finalTheme) {
          console.log('[MA-304][Phase1] No finalTheme - using old resolution logic');
          
        // MA-289: Update debug state with builder_theme info
        setDebugState(prev => ({
          ...prev,
          builderTheme: data?.builder_theme,
          themeSource: 'checking...'
        }));
        
        if (data.builder_theme) {
          console.log('[MA-287][Theme Resolution] builder_theme found in session:', data.builder_theme);
          console.log('[MA-287][CONVERT INPUT] builder_theme:', data.builder_theme);
          console.log('[MA-289][USER RUNTIME] builder_theme PRESENT in session response');
          
          const lpTokensFromBuilderTheme = convertThemeToLpTokens(data.builder_theme);
          
          console.log('[MA-287][CONVERT OUTPUT] lpTokensFromBuilderTheme:', lpTokensFromBuilderTheme);
          console.log('[MA-289][USER RUNTIME] builder_theme conversion result:', lpTokensFromBuilderTheme);
          
          // MA-287: STRICT VALIDATION - If conversion failed due to invalid theme, fall back to appearance
          if (!lpTokensFromBuilderTheme) {
            console.error('[MA-287][Theme Resolution] builder_theme validation FAILED - falling back to appearance');
            console.error('[MA-287][Theme Resolution] Invalid theme will NOT be used. Fix source in PPB.');
            console.error('[MA-289][USER RUNTIME] builder_theme INVALID - validation failed');
            
            // MA-289: Update debug state
            setDebugState(prev => ({
              ...prev,
              builderThemeValid: false,
              themeSource: 'appearance (builder_theme invalid)',
              renderBlocker: 'builder_theme validation failed'
            }));
            
            setLoading(false);
            // Continue to MA-186 fallback logic below
          } else {
            console.log('[MA-287][CONVERT OUTPUT TYPE]', typeof lpTokensFromBuilderTheme);
            console.log('[MA-287][CONVERT OUTPUT KEYS]', Object.keys(lpTokensFromBuilderTheme));
            console.log('[MA-289][USER RUNTIME] builder_theme VALID - will be used');
            
            // MA-289: Update debug state
            setDebugState(prev => ({
              ...prev,
              builderThemeValid: true,
              themeSource: 'builder_theme'
            }));
            
            // Store for theme resolution (will be used in tokens useMemo)
            setResolvedLpTokensFromBuilder(lpTokensFromBuilderTheme);
            setLoading(false);
            // Skip MA-186 client-side PP fetch when builder_theme exists and is valid
            console.log('[MA-287][Theme Resolution] Valid builder_theme applied - skipping MA-186 PP fetch');
            console.log('[MA-289][USER RUNTIME] theme source used: builder_theme');
            
            // Call onFunnelLoaded callback
            if (onFunnelLoaded) {
              onFunnelLoaded({
                funnelId: data.funnel_id,
                funnelName: data.funnel_name,
                settings: data.funnel_settings,
                appearance: data.appearance,
                brand_appearance: data.brand_appearance,
                builder_theme: data.builder_theme,
              });
            }
            // MA-297: Continue to conversion - do not early return
          }
        }
        
        // MA-186: Fetch PP custom theme if profit_path_id exists (FALLBACK when builder_theme not in session)
        if (data.profit_path_id) {
          console.log('[MA-186] profit_path_id detected:', data.profit_path_id, '- fetching PP theme');
          try {
            const ppResponse = await fetch(`${API_URL}/api/profit-paths/${data.profit_path_id}`);
            if (ppResponse.ok) {
              const ppData = await ppResponse.json();
              const foundation = ppData.builder_data?.foundation;
              const stylingMode = foundation?.theme_config?.styling_mode;
              
              if (stylingMode === 'custom') {
                const ppTheme = ppData.builder_data?.theme;
                if (ppTheme?.tokens) {
                  console.log('[MA-186] PP custom theme found:', ppTheme.name);
                  const lpTokensFromPP = convertThemeToLpTokens(ppTheme);
                  console.log('[MA-186] Converted lpTokens:', lpTokensFromPP);
                  
                  // Apply PP theme with highest priority
                  setAppearance(prev => ({
                    ...prev,
                    userColor: lpTokensFromPP.accent,
                    userTextColor: lpTokensFromPP.textOnAccent,
                    containerColor: lpTokensFromPP.cardBg,
                    threadColor: lpTokensFromPP.bgPage,
                    headerBackgroundColor: lpTokensFromPP.cardBg,
                    botTextColor: lpTokensFromPP.textPrimary,
                  }));
                  
                  // Set theme for tokens resolution
                  setFunnelTheme(resolveTheme({
                    preset: 'professional',
                    anchor_color: lpTokensFromPP.accent,
                    mode: isLpDark ? 'dark' : 'light'
                  }));
                  
                  setLoading(false);
                  // Continue to remaining settings and conversion logic
                }
              }
            }
          } catch (ppError) {
            console.warn('[MA-186] Failed to fetch PP theme:', ppError);
          }
        }
        
        } // END if (!data.finalTheme)
        
        setLoading(false);
        
        // Apply appearance settings from funnel config
        // MA-ARCH-186-P0-FIX: In LP dark mode, preserve LP theme accent colors
        // MA-256: DO NOT merge appearance.title or appearance.selectedIcon
        // These are legacy fields - header.title and header.icon_type are canonical
        if (data.appearance) {
          console.log('[ConversationFunnel] Applying appearance (theme only, not title/icon):', data.appearance);
          
          // MA-298: Store appearance in ref for canonical token resolution
          // Enables Canvas parity without React hoisting bug
          sessionAppearanceRef.current = data.appearance;
          
          if (lpTheme === 'lp_dark') {
            // Keep LP accent colors, only apply non-color appearance settings
            // MA-256: REMOVED title, titleIcon, selectedIcon merge - header is canonical
            setAppearance(prev => ({ 
              ...prev, 
              customIconUrl: data.appearance.customIconUrl || prev.customIconUrl,
            }));
          } else {
            // MA-256: Filter out legacy title/icon fields from appearance
            const { title, titleIcon, selectedIcon, ...themeOnlyAppearance } = data.appearance;
            setAppearance(prev => ({ ...prev, ...themeOnlyAppearance }));
          }
        }
        
        // MA-THEME-PHASE4: brand_appearance from session overrides stale stored appearance
        if (data.brand_appearance) {
          setAppearance(prev => ({
            ...prev,
            userColor: data.brand_appearance.userColor || data.brand_appearance.accent || prev.userColor,
            userTextColor: data.brand_appearance.userTextColor || prev.userTextColor,
            containerColor: data.brand_appearance.containerColor || prev.containerColor,
            threadColor: data.brand_appearance.threadColor || prev.threadColor,
            headerBackgroundColor: data.brand_appearance.headerBackgroundColor || prev.headerBackgroundColor,
            botTextColor: data.brand_appearance.botTextColor || prev.botTextColor,
            surfaceMode: data.brand_appearance.surfaceMode || prev.surfaceMode,
          }));
        } else if (brandConfig) {
          setAppearance(prev => ({
            ...prev,
            userColor: brandConfig.userColor || brandConfig.accent || prev.userColor,
            userTextColor: brandConfig.userTextColor || prev.userTextColor,
          }));
        }
          
        // MA-THEME-WIDGET-MIGRATION-012: Resolve theme via canonical engine
        // Backend may send brand_appearance (from themeToFunnelAppearance)
        // If not, resolve theme from appearance accent color or brandConfig
        // MA-FUNNEL-BUILDER-TEST-THEME-FIX-026: Use brand_appearance.surfaceMode for inherit_brand funnels
        if (!brandConfig && !lpTokens) {
          const ap = data.appearance || {};
          const brandAp = data.brand_appearance || {};
          const anchorColor = brandAp.accent || ap.userColor || ap.prospectColor || '#2563EB';
          const mode = brandAp.surfaceMode || (isLpDark ? 'dark' : 'light');
          const theme = resolveTheme({ preset: 'professional', anchor_color: anchorColor, mode });
          setFunnelTheme(theme);
        } else if (brandConfig && !lpTokens) {
          // Resolve from brandConfig (brand default_theme)
          // MA-FUNNEL-THEME-MODE-FIX-029: Check both surfaceMode (from brand_appearance) and mode (from brand config)
          const resolvedMode = brandConfig.surfaceMode || brandConfig.mode || (isLpDark ? 'dark' : 'light');
          console.log("[THEME DEBUG]", {
            surfaceMode: brandConfig?.surfaceMode,
            mode: brandConfig?.mode,
            resolvedMode,
          });
          const theme = resolveTheme({
            preset: brandConfig.preset || 'professional',
            anchor_color: brandConfig.anchor_color || brandConfig.accent || '#2563EB',
            mode: resolvedMode
          });
          setFunnelTheme(theme);
        }
        
        // MA-OPS-061: Notify parent about funnel load with settings
        if (onFunnelLoaded) {
          onFunnelLoaded({
            funnelId: data.funnel_id,
            funnelName: data.funnel_name,
            settings: data.funnel_settings,
            appearance: data.appearance,
            brand_appearance: data.brand_appearance,
          });
        }
        
        // MA-ARCH-187B-REFINEMENT: Resolve compact context from funnel metadata
        // MA-PPB-CONSENT-HEADER-074: Proper empty-string vs undefined handling
        
        // Pattern matching context (fallback for old funnels or undefined headers)
        const firstMsg = interpolateRuntime(
          data.current_node?.content?.message || '',
          data.collected_data || {},
          null
        );
        const ctx = resolveCompactContext(data.funnel_name, firstMsg, data.appearance, data.header);  // MA-256: Pass header
        
        // Title resolution: Match FunnelLivePreview semantics exactly
        // - If header.title is defined (even if empty), use it with "Assessment" fallback
        // - If header.title is undefined, use pattern matching
        const headerTitle = data.header?.title !== undefined
          ? (data.header.title.trim() || "Assessment")  // Defined: use value or "Assessment"
          : ctx.headerTitle;  // Undefined: use pattern matching
        
        // Icon resolution: Check all possible icon field paths
        // Priority: icon_type (canonical) > selectedIcon > icon > pattern matching
        const explicitIcon = data.header?.icon_type
          || data.header?.selectedIcon 
          || data.header?.icon;
        
        // Only use fallback if icon_type is explicitly "default", otherwise use the value
        const headerIcon = (explicitIcon && explicitIcon !== 'default') ? explicitIcon : (explicitIcon === 'default' ? 'shield' : ctx.headerIcon);
        
        // MA-PPB-CONSENT-HEADER-074: Debug instrumentation
        console.log('[ConversationFunnel] HEADER DEBUG:', {
          funnel_id: data.funnel_id,
          'data.header': data.header,
          'header.title_defined': data.header?.title !== undefined,
          'header.title_value': data.header?.title,
          'header.selectedIcon': data.header?.selectedIcon,
          'header.icon': data.header?.icon,
          'header.icon_type': data.header?.icon_type,
          'pattern_matched_title': ctx.headerTitle,
          'pattern_matched_icon': ctx.headerIcon,
          'FINAL_title': headerTitle,
          'FINAL_icon': headerIcon,
        });
        
        const resolvedContext = {
          ...ctx,
          headerTitle: headerTitle,
          headerIcon: headerIcon,
        };
        
        setResolvedContext(resolvedContext);
        
        // MA-297: Guard canonical conversion - only run if forced path didn't already handle it
        if (data.current_node && !initialStepAssigned) {
          console.log('[MA-291][PATH B1] Processing current_node from new session');
          console.log('[MA-291][PATH B1] node_type:', data.current_node.node_type);

          
          // Check for special node types that need different UI
          if (data.current_node.node_type === 'consent') {
            console.log('[MA-291][PATH B2-CONSENT] Early return for consent node');
            setConsentContent(data.current_node.content);
            setFunnelState('consent');
            setStepStartTime(Date.now());
            return;
          }
          
          if (data.current_node.node_type === 'booking') {
            console.log('[MA-291][PATH B2-BOOKING] Early return for booking node');
            setBookingContent(data.current_node.content);
            setBookingConfig({
              calendar_id: data.current_node.content?.config?.calendar_id,
              location_id: data.current_node.content?.config?.location_id,
            });
            setBookingNodeId(data.current_node.id);
            setFunnelState('booking');
            setStepStartTime(Date.now());
            return;
          }
          
          // Convert node to step format for existing UX
          // MA-283C: Get previous answer for variant resolution
          console.log('[MA-291][PATH B3] About to convert node to step - MAIN PATH');
          const previousAnswer = getPreviousAnswer(messages);
          
          // MA-290: LOG NODE BEFORE CONVERSION
          console.log('[MA-290][BEFORE CONVERT] data.current_node:', data.current_node);
          console.log('[MA-290][BEFORE CONVERT] node_type:', data.current_node?.node_type);
          console.log('[MA-290][BEFORE CONVERT] previousAnswer:', previousAnswer);
          
          // MA-295: TRACE CONVERSION EXECUTION
          console.log('[MA-295][CONVERT] About to call convertNodeToStep...');
          console.log('[MA-295][CONVERT] Input node:', JSON.stringify(data.current_node, null, 2));
          
          const step = convertNodeToStep(data.current_node, previousAnswer);
          
          // MA-295: TRACE CONVERSION RESULT
          console.log('[MA-295][CONVERT] convertNodeToStep returned:', step);
          console.log('[MA-295][CONVERT] Result is null?', step === null);
          console.log('[MA-295][CONVERT] Result is undefined?', step === undefined);
          
          // MA-290: LOG STEP AFTER CONVERSION
          console.log('[MA-290][AFTER CONVERT] step:', step);
          console.log('[MA-290][AFTER CONVERT] step.type:', step?.type);
          console.log('[MA-290][AFTER CONVERT] step.options:', step?.options);
          console.log('[MA-290][AFTER CONVERT] step.responses:', step?.responses);
          console.log('[MA-290][AFTER CONVERT] step.message:', step?.message);
          
          // MA-290: CRITICAL CHECK
          if (!step) {
            console.error('[MA-290][CRITICAL] convertNodeToStep returned null/undefined!');
            console.error('[MA-290][CRITICAL] Input node was:', data.current_node);
          }
          if (!step?.type) {
            console.error('[MA-290][CRITICAL] step.type is missing!');
            console.error('[MA-290][CRITICAL] step object:', step);
          }
          
          // MA-FUNNEL-TEST-001: Auto-advance past message nodes on initial load
          // Message nodes are non-interactive (no options) and should auto-continue
          if (data.current_node.node_type === 'message' && !data.current_node.content?.is_terminal) {
            // Show entry message first
            setMessages([{ 
              type: 'agent', 
              message: data.current_node.content?.message || '', 
              id: `msg-${Date.now()}` 
            }]);
            setLoading(false);
            
            // Auto-advance to the next node after a brief delay
            try {
              const advanceResponse = await fetch(`${API_URL}/api/funnel-sessions/${data.session_id}/respond`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  node_id: data.current_node.id,
                  response_value: 'continue'
                })
              });
              
              if (advanceResponse.ok) {
                const advanceData = await advanceResponse.json();
                
                if (advanceData.current_node) {
                  // MA-283C: Get previous answer for variant resolution
                  const previousAnswer = getPreviousAnswer(messages);
                  const nextStep = convertNodeToStep(advanceData.current_node, previousAnswer);
                  const nextMessage = interpolateRuntime(
                    advanceData.current_node.content?.message || '',
                    advanceData.collected_data || collectedResponses,
                    null
                  );
                  const nextMsgId = `msg-advance-${Date.now()}`;
                  
                  // Show typing indicator then the next question
                  setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: true });
                  await new Promise(r => setTimeout(r, 800));
                  
                  setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: false });
                  await new Promise(r => setTimeout(r, 200));
                  
                  setTransitioningMessage(null);
                  setMessages(prev => [...prev, { type: 'agent', message: nextMessage, id: nextMsgId }]);
                  
                  setCurrentStep(nextStep);
                  setCurrentStepId(advanceData.current_node.id);
                  setTimeout(() => setShowOptions(true), 100);
                  
                  onStepViewed?.({
                    stepId: advanceData.current_node.id,
                    stepType: nextStep.type,
                    sessionId: data.session_id
                  });
                }
              }
            } catch (advErr) {
              console.error('[ConversationFunnel] Auto-advance failed:', advErr);
            }
          } else {
            // OPTIMIZED: Show first question immediately (no typing delay on initial load)
            setMessages([{ 
              type: 'agent', 
              message: data.current_node.content?.message || '', 
              id: `msg-${Date.now()}` 
            }]);
            
            // MA-290: LOG BEFORE SET STEP
            console.log('[MA-290][SET STEP] About to call setCurrentStep with:', step);
            
            // MA-295: CRITICAL STATE UPDATE TRACE
            console.log('[MA-295][STATE] About to set currentStep to:', step);
            console.log('[MA-295][STATE] step is null?', step === null);
            console.log('[MA-295][STATE] step is undefined?', step === undefined);
            console.log('[MA-295][STATE] Step value that will become currentStep:', JSON.stringify(step, null, 2));
            
            // MA-293: Update execution fingerprint
            setExecutionFingerprint({
              path: 'B3-NEW-SESSION',
              convertCalled: window.MA293_CONVERT_CALLED ? 'YES' : 'NO',
              optionsCount: step?.options?.length || 0
            });
            
            setCurrentStep(step);
            setCurrentStepId(data.current_node.id);
            
            // MA-295: VERIFY STATE AFTER UPDATE
            console.log('[MA-295][STATE] setCurrentStep called');
            console.log('[MA-295][STATE] currentStepId set to:', data.current_node.id);
            
            // MA-290: LOG AFTER SET STEP
            console.log('[MA-290][SET STEP] setCurrentStep called. currentStepId set to:', data.current_node.id);
            
            // MA-289: USER RUNTIME LOGGING - Step state
            console.log('[MA-289][USER RUNTIME] ========== CURRENT STEP SET ==========');
            console.log('[MA-289][USER RUNTIME] currentStep:', step);
            console.log('[MA-289][USER RUNTIME] currentStep.type:', step?.type);
            console.log('[MA-289][USER RUNTIME] currentStep.options:', step?.options);
            console.log('[MA-289][USER RUNTIME] currentStep.message:', step?.message);
            
            // Short delay before showing options to let message render
            setTimeout(() => {
              setShowOptions(true);
              console.log('[MA-289][USER RUNTIME] showOptions set to TRUE');
            }, 50);
            
            // Emit step viewed event
            onStepViewed?.({
              stepId: data.current_node.id,
              stepType: step.type,
              sessionId: data.session_id
            });
          }
        } else {
          // MA-297: Canonical path skipped because forced path already handled conversion
          console.log('[MA-297][SKIP CANONICAL] Step already assigned by forced path, initialStepAssigned:', initialStepAssigned);
        }
        setStepStartTime(Date.now());
      } catch (err) {
        setError(err.message);
        onError?.(err);
        setLoading(false);
      }
    };

    // MA-EMBED-001: Init if we have either funnelSlug OR embedKey
    if (funnelSlug || embedKey) init();
  }, [funnelSlug, embedKey, attribution, testMode, retryCount]);

  // MA-295: Monitor currentStep changes to detect unexpected nullification
  useEffect(() => {
    console.log('[MA-295][WATCH] currentStep changed to:', currentStep);
    console.log('[MA-295][WATCH] currentStep is null?', currentStep === null);
    console.log('[MA-295][WATCH] currentStep is undefined?', currentStep === undefined);
    if (currentStep) {
      console.log('[MA-295][WATCH] currentStep.type:', currentStep.type);
      console.log('[MA-295][WATCH] currentStep.options?.length:', currentStep.options?.length);
      console.log('[MA-295][WATCH] currentStep.message:', currentStep.message);
    } else {
      console.log('[MA-295][WATCH] currentStep is falsy - Stack trace:');
      console.trace('[MA-295][WATCH] Stack for null/undefined currentStep');
    }
  }, [currentStep]);

  // ============================================================
  // Auto-scroll page when funnel expands (for embedded landing pages)
  // ============================================================
  const scrollFunnelIntoView = useCallback(() => {
    // For iframe embeds: notify parent window to scroll the iframe into view
    if (window.parent !== window) {
      try {
        window.parent.postMessage({
          type: 'MISSIONASSIST_SCROLL_INTO_VIEW',
          payload: { timestamp: Date.now() }
        }, '*');
      } catch (e) {
        console.warn('Could not send scroll message to parent');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // For non-iframe scenarios: scroll container into view with natural spacing above
    if (containerRef.current && window.parent === window) {
      const rect = containerRef.current.getBoundingClientRect();
      const offset = 40; // px of breathing room above the funnel
      const targetY = window.scrollY + rect.top - offset;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }
  }, []);

  // MA-ARCH-187B: Notify parent iframe about height changes
  const notifyParentResize = useCallback((height) => {
    if (window.parent !== window) {
      try {
        window.parent.postMessage({
          type: 'MISSIONASSIST_RESIZE_IFRAME',
          payload: { height: Math.ceil(height) }
        }, '*');
      } catch (e) {
        console.warn('Could not send resize message to parent');
      }
    }
  }, []);

  // Trigger page scroll + iframe resize when layout expands (after first user interaction)
  useEffect(() => {
    if (layoutMode === 'expanded' && hasUserInteracted) {
      // MA-ARCH-187B: Notify parent to resize iframe to expanded height
      const expandedHeight = LAYOUT.HEADER_HEIGHT + LAYOUT.EXPANDED_TOTAL_CONTENT_HEIGHT + 16;
      notifyParentResize(expandedHeight);
      
      // Small delay to let the expansion animation start, then scroll
      const timer = setTimeout(() => {
        scrollFunnelIntoView();
        // MA-FUNNEL-UI-072: Exclude consent-enabled inputs from auto-scroll to keep question visible
        const isConsentStep = currentStep?.requires_consent && currentStep?.type?.includes('input');
        if (!isConsentStep) {
          scrollToBottom();
        }
      }, 250);
      return () => clearTimeout(timer);
    }
  }, [layoutMode, hasUserInteracted, scrollFunnelIntoView, scrollToBottom, notifyParentResize, currentStep?.id, currentStep?.requires_consent]);

  // Trigger page scroll when transitioning to booking state (after consent)
  useEffect(() => {
    if (funnelState === 'booking') {
      // Delay to let the booking view render, then scroll into view
      const timer = setTimeout(scrollFunnelIntoView, 300);
      return () => clearTimeout(timer);
    }
  }, [funnelState, scrollFunnelIntoView]);

  // ============================================================
  // Submit response - Uses Session Engine API
  // POST /api/funnel-sessions/{session_id}/respond
  // Backend determines next node via edge evaluation
  // MA-PHONE-CONSENT-037: Added options parameter for consent_given flag
  // ============================================================
  const submitResponse = useCallback(async (value, displayValue = null, options = {}) => {
    if (!currentStep || submitting) return;
    
    // MA-283C-315: Helper to normalize answer for variant selection
    // Extracts yes/no from the current submitted value
    const normalizeAnswer = (val) => {
      if (!val || typeof val !== 'string') return null;
      const normalized = val.toLowerCase();
      if (normalized === 'yes' || normalized.startsWith('yes')) return 'yes';
      if (normalized === 'no' || normalized.startsWith('no')) return 'no';
      return null;
    };
    
    // Mark that user has interacted
    setHasUserInteracted(true);
    
    setSubmitting(true);
    setShowOptions(false);
    const timeOnStep = Date.now() - stepStartTime;

    // Add user message
    const userMessage = displayValue || value;
    setMessages(prev => [...prev, { type: 'user', message: userMessage, id: `msg-${Date.now()}` }]);
    
    // Collect response for contact info (local copy for booking step)
    if (currentStep.field_name) {
      setCollectedResponses(prev => ({
        ...prev,
        [currentStep.field_name]: value
      }));
    }
    
    // EXPAND layout after first response
    if (layoutMode === 'compact') {
      setLayoutMode('expanded');
    }

    // Brief pause before showing typing indicator
    await new Promise(r => setTimeout(r, 400));

    try {
      // Build request body for Session Engine
      const requestBody = {
        node_id: currentStep.id,
        field_name: currentStep.field_name,
        // MA-TF-CLAIM-001: Include TrustedForm cert URL for claiming at submit step
        trustedform_cert_url: trustedFormUrl || null,
      };
      
      // For question nodes (quick_reply), send response_id and value
      if (currentStep.type === 'quick_reply') {
        // Find the option that was selected
        const selectedOption = currentStep.options?.find(opt => 
          opt.value === value || opt.label === value
        );
        requestBody.response_id = selectedOption?.id || value;
        requestBody.response_value = selectedOption?.value || value;
      } else {
        // For input nodes, send input_value
        requestBody.input_value = value;
      }
      
      // MA-PHONE-CONSENT-037: Include consent_given flag if provided
      if (options.consent_given !== undefined) {
        requestBody.consent_given = options.consent_given;
      }
      
      const response = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      // MA-CONSENT-ROUTING-FIX-050: Debug logging for runtime investigation
      console.log('[MA-CONSENT-ROUTING-FIX-050] Submit request:', {
        sessionId,
        nodeId: currentStep.id,
        requestBody
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[MA-CONSENT-ROUTING-FIX-050] Submit failed:', errorData);
        throw new Error(errorData.detail || 'Failed to submit');
      }
      
      const data = await response.json();
      
      // MA-CONSENT-ROUTING-FIX-050: Log backend response
      console.log('[MA-CONSENT-ROUTING-FIX-050] Backend response:', {
        currentNode: data.current_node,
        nextNodeId: data.current_node?.id,
        isTerminal: data.is_terminal,
        status: data.status,
        leadId: data.lead_id
      });
      
      // MA-BOOK-008: Capture collected_data IMMEDIATELY for use in booking transition
      // React state updates are async, so we need this data available synchronously
      const latestCollectedData = data.collected_data || collectedResponses;
      
      // Update local collected data from session
      if (data.collected_data) {
        setCollectedResponses(data.collected_data);
      }
      
      // MA-FUNNEL-LEAD-001: Update lead_id if returned from submit step
      if (data.lead_id) {
        setLeadId(data.lead_id);
      }
      
      // Emit step completed event
      onStepCompleted?.({
        stepId: currentStep.id,
        stepType: currentStep.type,
        fieldName: currentStep.field_name,
        value: value,
        timeOnStepMs: timeOnStep,
        sessionId
      });

      // Check for consent node transition
      if (data.current_node?.node_type === 'consent') {
        // Transition to consent step - show final acknowledgement then consent form
        const consentNode = data.current_node;
        const finalAck = consentNode.content?.message || "Perfect — just one more step.";
        const finalId = `ack-consent-${Date.now()}`;
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: true });
        await new Promise(r => setTimeout(r, 700));
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: false });
        await new Promise(r => setTimeout(r, 250));
        
        setTransitioningMessage(null);
        setMessages(prev => [...prev, { type: 'agent', message: finalAck, id: finalId }]);
        
        // Store consent node content for the ConsentStep component
        setConsentContent(consentNode.content);
        
        await new Promise(r => setTimeout(r, 600));
        setFunnelState('consent');
        setCurrentStep(null);
        setSubmitting(false);
        return;
      }
      
      // Check for booking node transition
      if (data.current_node?.node_type === 'booking') {
        // Transition to booking step
        const finalAck = "Perfect — let's get you scheduled.";
        const finalId = `ack-booking-${Date.now()}`;
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: true });
        await new Promise(r => setTimeout(r, 700));
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: false });
        await new Promise(r => setTimeout(r, 250));
        
        setTransitioningMessage(null);
        setMessages(prev => [...prev, { type: 'agent', message: finalAck, id: finalId }]);
        
        await new Promise(r => setTimeout(r, 600));
        
        // Extract booking config from node
        const bookingNode = data.current_node;
        setBookingConfig({
          calendar_link: bookingNode.content?.calendar_link,
          helper_text: bookingNode.content?.helper_text,
          calendar_id: bookingNode.content?.config?.calendar_id,  // MA-ARCH-082: From booking node config
          location_id: bookingNode.content?.config?.location_id,
        });
        setBookingContent(bookingNode.content);  // MA-UI-097: Store full content for display settings
        setBookingNodeId(bookingNode.id);  // MA-ARCH-082: Store node ID for booking events
        onBookingStart?.({ ...bookingNode.content, ...latestCollectedData });  // MA-BOOK-008: Use fresh data
        setFunnelState('booking');
        setCurrentStep(null);
        setSubmitting(false);
        return;
      }
      
      // Check if session is complete (terminal node)
      if (data.is_terminal || data.status === 'completed') {
        // Show terminal message if present
        const terminalMessage = data.current_node?.content?.message;
        const outcome = data.outcome || data.current_node?.content?.outcome;
        
        // Show final acknowledgement
        const finalAck = outcome === 'not_qualified' 
          ? terminalMessage || "Thanks for your interest!"
          : "Perfect — you're all set.";
        const finalId = `ack-final-${Date.now()}`;
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: true });
        await new Promise(r => setTimeout(r, 700));
        
        setTransitioningMessage({ id: finalId, message: finalAck, isTyping: false });
        await new Promise(r => setTimeout(r, 250));
        
        setTransitioningMessage(null);
        setMessages(prev => [...prev, { type: 'agent', message: finalAck, id: finalId }]);
        
        await new Promise(r => setTimeout(r, 600));
        
        // Clear session from localStorage on completion
        localStorage.removeItem(`funnel_session_${funnelSlug}`);
        
        // Emit completion
        onComplete?.({ sessionId, leadId, outcome });
        setCurrentStep(null);
        setSubmitting(false);
        return;
      }
      
      // Continue to next step
      if (data.current_node) {
        const nextNode = data.current_node;
        // MA-283C-315: Use current submitted value for immediate next step
        const previousAnswer = normalizeAnswer(value) || getPreviousAnswer(messages);
        
        const nextStep = convertNodeToStep(nextNode, previousAnswer);
        
        // MA-283C-328: Use RESOLVED message from nextStep
        const nextMessage = interpolateRuntime(
          nextStep.message || '',
          collectedResponses,
          null
        );
        
        // CONVERSATIONAL FLOW with smooth transitions
        const hasResponseOptions = currentStep.type === 'quick_reply' || (currentStep.options && currentStep.options.length > 0);
        
        if (hasResponseOptions) {
          // MA-283C-329: Response-aware acknowledgement selection
          const nodeAck = currentStep._node?.content?.acknowledgement;
          
          let ack;
          if (nodeAck) {
            // Node has custom acknowledgement
            ack = nodeAck;
          } else {
            // Select acknowledgement based on previous answer
            const previousAnswer = normalizeAnswer(value);
            
            if (previousAnswer === 'yes') {
              ack = ACKNOWLEDGEMENTS_AFTER_YES[Math.floor(Math.random() * ACKNOWLEDGEMENTS_AFTER_YES.length)];
            } else if (previousAnswer === 'no') {
              ack = ACKNOWLEDGEMENTS_AFTER_NO[Math.floor(Math.random() * ACKNOWLEDGEMENTS_AFTER_NO.length)];
            } else {
              ack = ACKNOWLEDGEMENTS_DEFAULT[Math.floor(Math.random() * ACKNOWLEDGEMENTS_DEFAULT.length)];
            }
          }
          
          const ackId = `ack-${Date.now()}`;
          
          // Start with typing indicator (smooth transition approach)
          setTransitioningMessage({ id: ackId, message: ack, isTyping: true });
          
          // Show typing for ~800-1000ms
          await new Promise(r => setTimeout(r, 800 + Math.random() * 200));
          
          // Smoothly transition typing → acknowledgement text
          setTransitioningMessage({ id: ackId, message: ack, isTyping: false });
          
          // Wait for transition animation (~200ms) then commit to messages
          await new Promise(r => setTimeout(r, 250));
          setTransitioningMessage(null);
          setMessages(prev => [...prev, { type: 'agent', message: ack, id: ackId }]);
          
          // Pause after acknowledgement (~500ms)
          await new Promise(r => setTimeout(r, 500 + Math.random() * 150));
          
          // Start typing for next question
          const nextMsgId = `msg-${Date.now()}`;
          setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: true });
          
          // Show typing for next question (~700-900ms)
          await new Promise(r => setTimeout(r, 700 + Math.random() * 200));
          
          // Smoothly transition typing → question text
          setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: false });
          
          // Wait for transition then commit
          await new Promise(r => setTimeout(r, 250));
          setTransitioningMessage(null);
          setMessages(prev => [...prev, { type: 'agent', message: nextMessage, id: nextMsgId }]);
          
        } else {
          // For text inputs - no acknowledgement, just next question
          const nextMsgId = `msg-${Date.now()}`;
          setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: true });
          
          // Show typing (~800-1000ms)
          await new Promise(r => setTimeout(r, 800 + Math.random() * 200));
          
          // Smoothly transition typing → question text
          setTransitioningMessage({ id: nextMsgId, message: nextMessage, isTyping: false });
          
          // Wait for transition then commit
          await new Promise(r => setTimeout(r, 250));
          setTransitioningMessage(null);
          setMessages(prev => [...prev, { type: 'agent', message: nextMessage, id: nextMsgId }]);
        }
        
        // MA-FUNNEL-TEST-001: Auto-advance past mid-flow message nodes
        if (nextNode.node_type === 'message' && !nextNode.content?.is_terminal) {
          // Message node mid-flow: show message, then auto-advance to next interactive node
          try {
            const midAdvance = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}/respond`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ node_id: nextNode.id, response_value: 'continue' })
            });
            
            if (midAdvance.ok) {
              const midData = await midAdvance.json();
              
              if (midData.is_terminal || midData.status === 'completed') {
                const termMsg = midData.current_node?.content?.message || "You're all set!";
                const termId = `term-${Date.now()}`;
                setTransitioningMessage(null);
                setMessages(prev => [...prev, { type: 'agent', message: termMsg, id: termId }]);
                localStorage.removeItem(`funnel_session_${funnelSlug}`);
                onComplete?.({ sessionId, leadId, outcome: midData.outcome });
                setCurrentStep(null);
                setSubmitting(false);
                return;
              }
              
              if (midData.current_node) {
                // MA-283C-315 FIX: For mid-flow auto-advance, use current value
                // This is part of the same submission flow, so we use the fresh answer
                const previousAnswer = normalizeAnswer(value) || getPreviousAnswer(messages);
                
                const realNextStep = convertNodeToStep(midData.current_node, previousAnswer);
                const realNextMsg = midData.current_node.content?.message || '';
                const realNextId = `msg-mid-${Date.now()}`;
                
                setTransitioningMessage({ id: realNextId, message: realNextMsg, isTyping: true });
                await new Promise(r => setTimeout(r, 700));
                setTransitioningMessage({ id: realNextId, message: realNextMsg, isTyping: false });
                await new Promise(r => setTimeout(r, 200));
                setTransitioningMessage(null);
                setMessages(prev => [...prev, { type: 'agent', message: realNextMsg, id: realNextId }]);
                
                setCurrentStep(realNextStep);
                setCurrentStepId(midData.current_node.id);
                setStepStartTime(Date.now());
                setTimeout(() => {
                  setShowOptions(true);
                  setSubmitting(false);
                  onStepViewed?.({
                    stepId: midData.current_node.id,
                    stepType: realNextStep.type,
                    sessionId
                  });
                }, 150);
                return;
              }
            }
          } catch (midErr) {
            console.error('[ConversationFunnel] Mid-flow auto-advance failed:', midErr);
          }
        }
        
        // Update step and show options
        setCurrentStep(nextStep);
        setCurrentStepId(nextNode.id);
        setStepStartTime(Date.now());
        setTimeout(() => {
          setShowOptions(true);
          setSubmitting(false);
          onStepViewed?.({
            stepId: nextNode.id,
            stepType: nextStep.type,
            sessionId
          });
        }, 150);
      }
    } catch (err) {
      setTransitioningMessage(null);
      setError(err.message);
      onError?.(err);
      setSubmitting(false);
      setShowOptions(true);
    }
  }, [currentStep, submitting, stepStartTime, sessionId, funnelSlug, layoutMode, collectedResponses, onStepCompleted, onStepViewed, onComplete, onBookingStart, onError, leadId]);

  // ============================================================
  // Handle consent - Uses Session Engine API
  // Advances session through consent node with consent_given=true
  // ============================================================
  const handleConsent = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      // Get current session to find the consent node
      const sessionResponse = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}`);
      if (!sessionResponse.ok) {
        throw new Error(`Session lookup failed (${sessionResponse.status})`);
      }
      const sessionData = await sessionResponse.json();
      
      // Find the consent node ID (should be current or we need to advance to it)
      // The consent node should have been set when we transitioned to consent state
      const consentNodeId = sessionData.current_node?.id || 'consent';
      
      // Submit consent response via Session Engine
      const response = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          node_id: consentNodeId,
          consent_given: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to submit consent');
      }
      
      const data = await response.json();
      
      // MA-BOOK-008: Capture collected_data IMMEDIATELY for use in booking transition
      // React state updates are async, so we need this data available synchronously
      const latestCollectedData = data.collected_data || collectedResponses;
      
      // Update collected data from session
      if (data.collected_data) {
        setCollectedResponses(data.collected_data);
      }
      
      // Emit consent given event
      onConsentGiven?.({
        sessionId,
        trustedFormUrl,
        timestamp: new Date().toISOString()
      });
      
      // Check if next node is booking
      if (data.current_node?.node_type === 'booking') {
        const bookingNode = data.current_node;
        setBookingConfig({
          calendar_link: bookingNode.content?.calendar_link,
          helper_text: bookingNode.content?.helper_text,
          calendar_id: bookingNode.content?.config?.calendar_id,  // MA-ARCH-082
          location_id: bookingNode.content?.config?.location_id,
        });
        setBookingContent(bookingNode.content);  // MA-UI-097: Store full content for display settings
        setBookingNodeId(bookingNode.id);  // MA-ARCH-082: Store node ID for booking events
        setFunnelState('booking');
        onBookingStart?.({
          ...bookingNode.content,
          ...latestCollectedData  // MA-BOOK-008: Use the freshly fetched data, not stale closure
        });
      } else if (data.is_terminal || data.status === 'completed') {
        // Session completed after consent
        localStorage.removeItem(`funnel_session_${funnelSlug}`);
        onComplete?.({ sessionId, outcome: data.outcome });
      } else {
        // Continue to next step after consent
        const nextStep = convertNodeToStep(data.current_node);
        setCurrentStep(nextStep);
        setCurrentStepId(data.current_node.id);
        setFunnelState('chat');
        setShowOptions(true);
      }
      
      setSubmitting(false);
    } catch (err) {
      setError(err.message);
      onError?.(err);
      setSubmitting(false);
    }
  }, [submitting, sessionId, funnelSlug, trustedFormUrl, collectedResponses, onBookingStart, onConsentGiven, onComplete, onError]);

  // ============================================================
  // MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Structured Layout for Consent-Enabled Input Steps
  // Renders thread, input, consent, and CTA as separate sections (matches builder structure)
  // ============================================================
  const renderStructuredConsentLayout = () => {
    if (!currentStep) return null;
    
    const handleInputChange = (e) => {
      setStructuredInputValue(e.target.value);
    };
    
    const handleInputKeyPress = (e) => {
      // Don't submit on Enter for consent-enabled inputs
      // User must check consent and click CTA
    };
    
    const handleConsentSubmit = ({ consent_given }) => {
      // Single submission: combine input value + consent flag
      if (!structuredInputValue.trim()) {
        return; // Input validation
      }
      submitResponse(structuredInputValue, null, { consent_given });
    };
    
    // Get prefill value
    const prefillValue = currentStep.field_name ? collectedResponses[currentStep.field_name] : '';
    const displayValue = structuredInputValue || prefillValue;
    
    // MA-FUNNEL-UI-072: Use consistent response area background matching other steps
    // FIX: Use tokens.responseBg (calibrated #f1f5f9) instead of containerBg (white #ffffff)
    const responseAreaBg = tokens.surfaceMode === 'light'
      ? (tokens.responseBg || tokens.containerBg || appearance.containerColor)
      : (tokens.responseAreaBg || appearance.responseAreaColor || getContrastingShade(appearance.containerColor));
    
    // MA-FUNNEL-UI-072: Use fixed thread height matching standard input steps
    // Regular input steps: thread = 400 - 80 = 320px
    // Consent steps expand the TOTAL container to accommodate the large consent block
    const consentThreadHeight = 320; // Match standard input step thread height
    
    // MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Flex-based layout within motion.div container
    // Thread uses motion.div with animation to preserve scroll position during expansion
    return (
      <div className="flex flex-col">
        {/* SECTION 1: Thread Area - Animated height matching standard input steps */}
        <motion.div 
          ref={threadRef}
          className="overflow-y-auto overflow-x-hidden px-4 py-3"
          initial={false}
          animate={{ height: consentThreadHeight }}
          transition={{ duration: 0.26, ease: [0.25, 0.1, 0.25, 1] }}
          style={{ 
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            backgroundColor: tokens.threadBg || appearance.threadColor,
          }}
        >
          <div 
            className="flex flex-col justify-end"
            style={{ minHeight: '100%', gap: `${DS.space.md}px` }}
          >
            {/* Messages */}
            {messages.map((msg) => (
              msg.type === 'agent' 
                ? <AgentMessage key={msg.id} message={msg.message} tokens={tokens} />
                : <UserMessage 
                    key={msg.id} 
                    message={msg.message} 
                    userColor={appearance.userColor}
                    userTextColor={appearance.userTextColor}
                  />
            ))}
            {/* Transitioning message */}
            {transitioningMessage && (
              <TransitioningAgentMessage 
                key={transitioningMessage.id}
                message={transitioningMessage.message}
                isTyping={transitioningMessage.isTyping}
              />
            )}
          </div>
        </motion.div>
        
        {/* SECTION 2: Input Field Section */}
        <div 
          className="px-4 py-3"
          style={{ 
            backgroundColor: responseAreaBg,
            borderTopColor: tokens.borderSubtle || '#E5E7EB',
          }}
        >
          <InputFieldOnly
            step={currentStep}
            value={displayValue}
            onChange={handleInputChange}
            onKeyPress={handleInputKeyPress}
            disabled={submitting}
            inputColor={appearance.userColor}
            tokens={tokens}
          />
        </div>
        
        {/* SECTION 3: Consent Section */}
        <div 
          className="px-4 py-3"
          style={{ 
            backgroundColor: responseAreaBg,
            borderTopColor: tokens.borderSubtle || '#E5E7EB',
          }}
        >
          <ConsentBlock
            consentText={currentStep.consent_text}
            submitButtonText={currentStep.submit_button_text}
            onSubmit={handleConsentSubmit}
            disabled={submitting}
            buttonColor={appearance.userColor}
            buttonTextColor={tokens.optionText}
            tokens={tokens}
            inputValue={structuredInputValue}
            stepType={currentStep.type}
          />
        </div>
      </div>
    );
  };

  // ============================================================
  // Render response area content (inside fixed container)
  // ============================================================
  // MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Callback for consent height measurement
  const handleConsentHeightMeasured = useCallback((height) => {
    setConsentBlockHeight(height);
  }, []);

  const renderResponseContent = () => {
    // MA-288: LOG RENDER BRANCH
    console.log('[MA-288][RENDER RESPONSE]', {
      currentStep,
      currentStepType: currentStep?.type,
      transitioningMessage,
      showOptions,
      submitting
    });
    
    // Hide response options while transitioning (showing typing indicator)
    if (!currentStep || transitioningMessage || !showOptions) {
      console.log('[MA-288][RENDER NULL]', { 
        reason: !currentStep ? 'NO_CURRENT_STEP' : (!showOptions ? 'OPTIONS_HIDDEN' : 'TRANSITIONING')
      });
      return null;
    }

    // MA-288: HARD RENDER TEST
    console.log('[MA-288][ABOUT TO RENDER STEP]', currentStep.type);
    
    // MA-288: SWITCH TRACE
    console.log('[MA-288][SWITCH CASE]', currentStep.type);

    if (currentStep.type === 'quick_reply') {
      console.log('[MA-288][CASE HIT] quick_reply');
      
      return (
        <ReplyChips
          key={`chips-${currentStepId}`}
          options={currentStep.options}
          onSelect={(opt) => submitResponse(opt.value, opt.label)}
          disabled={submitting}
          stepId={currentStepId}
          userColor={appearance.userColor}
          textColor={tokens.optionText}
        />
      );
    }
    
    // MA-288: Check for input types
    if (currentStep.type?.includes('input') || currentStep.type === 'text_input' || 
        currentStep.type === 'phone_input' || currentStep.type === 'email_input' || 
        currentStep.type === 'zip_input') {
      console.log('[MA-288][CASE HIT] input type');
      
      // Get prefill value for this field
      const prefillValue = currentStep.field_name ? collectedResponses[currentStep.field_name] : '';

      return (
        <TextInputStep
          key={`input-${currentStepId}`}
          step={currentStep}
          onSubmit={(v, opts) => submitResponse(v, null, opts)}
          disabled={submitting}
          prefillValue={prefillValue}
          inputColor={appearance.userColor}
          buttonTextColor={tokens.optionText}
          tokens={tokens}
          onConsentHeightMeasured={handleConsentHeightMeasured}
        />
      );
    }

    // MA-288: HARD FAIL for unknown node types (NO SILENT NULL)
    console.error('[MA-288][UNKNOWN NODE TYPE]', currentStep);
    return (
      <div style={{ padding: '20px', color: 'white', backgroundColor: 'red', fontWeight: 'bold' }}>
        <div style={{ fontSize: '16px' }}>ERROR: Unknown node type</div>
        <div style={{ fontSize: '12px', marginTop: '8px' }}>
          Type: {currentStep?.type || 'undefined'}
        </div>
        <div style={{ fontSize: '11px', marginTop: '4px', fontFamily: 'monospace' }}>
          {JSON.stringify(currentStep, null, 2)}
        </div>
      </div>
    );
  };

  // ============================================================
  // Container styles - stable, centered, no transforms
  // ============================================================
  const containerStyles = {
    width: '100%',
    maxWidth: isLpDark ? '580px' : `${LAYOUT.CONTAINER_WIDTH}px`,
    margin: '0 auto',
    transform: 'none',
    position: 'relative'
  };

  // MA-ARCH-183B: Card styles from tokens
  // MA-LP-LAYOUT-027E: Funnel card IS the elevated element — no outer wrapper shadow
  const cardStyles = {
    backgroundColor: tokens.containerBg,
    borderRadius: tokens.radiusLg,
    overflow: 'hidden',
    width: '100%',
    boxShadow: isLpDark
      ? '0 16px 48px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)'
      : tokens.cardShadowPrimary,
    border: isLpDark ? `1px solid rgba(255,255,255,0.04)` : undefined,
  };

  // ============================================================
  // MA-PPB-FUNNEL-RENDER-LOAD-084: Stable First-Paint Loading State
  // Uses high-fidelity FunnelSkeleton with pre-initialized LP tokens
  // This ensures 1 visual state (skeleton) → 1 real state (loaded funnel)
  // NO partial renders, NO fallback icon flashes, NO white seams
  // ============================================================
  if (loading) {
    return (
      <div 
        className={`py-1 px-2 lp-funnel-themed ${className}`}
        style={{ background: 'transparent', backgroundColor: 'transparent' }}
      >
        <TypingStyles />
        <ThemeStyleInjection tokens={tokens} />
        <div ref={containerRef} style={{...containerStyles, background: 'transparent'}}>
          {/* MA-PPB-FUNNEL-RENDER-LOAD-084: High-fidelity skeleton with LP tokens */}
          <FunnelSkeleton 
            lpTokens={tokens}
            isLpDark={isLpDark}
            height={LAYOUT.COMPACT_CARD_TOTAL_HEIGHT}
          />
        </div>
      </div>
    );
  }

  // ============================================================
  // Error state
  // ============================================================
  if (error) {
    const isPublishError = error.toLowerCase().includes('published') || error.toLowerCase().includes('publish');
    const errorTitle = isPublishError ? 'Funnel Not Published' : 'Something went wrong';
    const errorMessage = isPublishError 
      ? 'This funnel needs to be published before it can be used in production. Use test_mode=true in the URL to test with draft content.'
      : error;
    
    return (
      <div 
        className={`py-2 px-3 lp-funnel-themed ${className}`}
        style={{ background: 'transparent', backgroundColor: 'transparent' }}
      >
        <TypingStyles />
        <ThemeStyleInjection tokens={tokens} />
        <div ref={containerRef} style={{...containerStyles, background: 'transparent'}}>
          <div style={cardStyles}>
            <FunnelHeader appearance={appearance} isLpDark={isLpDark} resolvedContext={resolvedContext} tokens={tokens} />
            <div className="p-6 text-center">
              <p className="lp-df-error-title font-medium" style={{ color: '#f87171' }}>{errorTitle}</p>
              <p className="lp-df-error-msg mt-2 text-sm" style={{ color: tokens.textMuted }}>{errorMessage}</p>
              <button
                data-testid="funnel-retry-btn"
                onClick={() => { setError(null); setLoading(true); setRetryCount(c => c + 1); }}
                style={{
                  marginTop: 16, padding: '8px 20px', fontSize: 13, fontWeight: 500,
                  borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: tokens.accent, color: tokens.textInverse,
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Consent state
  // ============================================================
  if (funnelState === 'consent') {
    return (
      <div 
        className={`py-4 px-4 lp-funnel-themed ${className}`} 
        data-testid="conversation-funnel"
        style={{ background: 'transparent', backgroundColor: 'transparent' }}
      >
        <TypingStyles />
        <ThemeStyleInjection tokens={tokens} />
        <div ref={containerRef} style={{...containerStyles, background: 'transparent'}}>
          <div style={cardStyles}>
            <FunnelHeader appearance={appearance} isLpDark={isLpDark} resolvedContext={resolvedContext} tokens={tokens} />
            <ConsentStep 
              onAccept={handleConsent} 
              disabled={submitting} 
              content={(() => {
                const base = consentContent || {};
                // MA-LP-CONSENT-ENRICH: If session consent text is empty, use override from LP data
                if (!base.consent_text && consentTextOverride) {
                  return { ...base, consent_text: consentTextOverride };
                }
                return consentContent;
              })()}
              accentColor={tokens.accent}
              tokens={tokens}
            />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Booking state - TRANSPARENT container, no card shell
  // The QuickBookingWidget handles its own card styling
  // ============================================================
  if (funnelState === 'booking') {
    // MA-LP-BOOKING-EMAIL-FIX: Handle missing email for AI-generated funnels
    // GHL requires email, but older AI funnels may not collect it
    // Use placeholder email if missing to prevent booking failure
    const contactInfo = {
      first_name: collectedResponses.first_name || '',
      last_name: collectedResponses.last_name || '',
      email: collectedResponses.email || `noemail+${sessionId}@placeholder.local`,
      phone: collectedResponses.phone || ''
    };
    
    // MA-ARCH-082: Build funnel context for booking events
    // MA-FUNNEL-LEAD-001: Extended with lead_id, journey_id, tenant_id for dispatch logging
    const funnelContext = (funnelId && sessionId && bookingNodeId) ? {
      funnel_id: funnelId,
      funnel_name: null,
      session_id: sessionId,
      node_id: bookingNodeId,
      lead_id: leadId || null,  // From session state
      journey_id: journeyId || null,  // From session state
      tenant_id: tenantId || null,  // From session or funnel
    } : null;
    
    // MA-FUNNEL-ROUTING-052: All routing is explicit. Only real node IDs.
    // - null → no route (terminal / dead end)
    // - <node_id> → explicit target step (may be a terminal node)
    // No __exit_* aliases — all routing resolved before runtime.
    const handleBookingNextStep = async (nextStepId) => {
      console.log('[ConversationFunnel] Booking completed, routing to:', nextStepId);
      
      // Terminal booking (no next step, or stale __exit alias as defensive fallback)
      if (!nextStepId || nextStepId.startsWith("__exit") || nextStepId.startsWith("__")) {
        if (nextStepId?.startsWith("__exit")) {
          console.error('[MA-FUNNEL-ROUTING-052] STALE_EXIT_ALIAS in widget:', nextStepId);
        }
        console.log('[ConversationFunnel] Booking is terminal, completing funnel');
        localStorage.removeItem(`funnel_session_${funnelSlug}`);
        onComplete?.({ sessionId, outcome: 'booking_completed' });
        return;
      }
      
      // Navigate to next step via session API
      try {
        const sessionResponse = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}`);
        if (!sessionResponse.ok) {
          console.error('[ConversationFunnel] Failed to fetch session after booking');
          return;
        }
        
        const sessionData = await sessionResponse.json();
        
        // Advance the session to the next node
        const advanceResponse = await fetch(`${API_URL}/api/funnel-sessions/${sessionId}/respond`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            node_id: bookingNodeId,
            booking_completed: true,
            next_node_id: nextStepId  // Tell session engine where to go
          })
        });
        
        if (!advanceResponse.ok) {
          console.error('[ConversationFunnel] Failed to advance session after booking');
          // Still try to show something meaningful
          localStorage.removeItem(`funnel_session_${funnelSlug}`);
          onComplete?.({ sessionId, outcome: 'booking_completed' });
          return;
        }
        
        const advanceData = await advanceResponse.json();
        
        // Check what type of node we're transitioning to
        if (advanceData.is_terminal || advanceData.status === 'completed') {
          localStorage.removeItem(`funnel_session_${funnelSlug}`);
          onComplete?.({ sessionId, outcome: advanceData.outcome || 'booking_completed' });
        } else if (advanceData.current_node) {
          // Navigate to the next step
          const nextStep = convertNodeToStep(advanceData.current_node);
          setCurrentStep(nextStep);
          setCurrentStepId(advanceData.current_node.id);
          setFunnelState('chat');
          setShowOptions(true);
        }
      } catch (err) {
        console.error('[ConversationFunnel] Error navigating after booking:', err);
        // Graceful fallback - complete the funnel
        localStorage.removeItem(`funnel_session_${funnelSlug}`);
        onComplete?.({ sessionId, outcome: 'booking_completed' });
      }
    };
    
    return (
      <div 
        className={`py-1 px-2 lp-funnel-themed ${className}`} 
        data-testid="conversation-funnel"
        data-funnel-state="booking"
        style={{ 
          background: 'transparent', 
          backgroundColor: 'transparent',
        }}
      >
        <TypingStyles />
        <ThemeStyleInjection tokens={tokens} />
        {/* MA-PPB-FUNNEL-062: Booking renders in a themed card matching the funnel container */}
        <div style={{ maxWidth: containerStyles.maxWidth, margin: '0 auto', padding: '16px 0' }}>
          <div style={{
            backgroundColor: tokens.containerBg,
            borderRadius: tokens.radiusLg || '16px',  // FIX: Don't add px since tokens.radiusLg already includes it
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: cardStyles.boxShadow,
            overflow: 'hidden',
          }}>
            <BookingStep 
              bookingConfig={bookingConfig} 
              content={bookingContent}
              contactInfo={contactInfo}
              funnelContext={funnelContext}
              tokens={tokens}
              accentColor={appearance.userColor || tokens.primary}
              onSuccess={(result) => {
                onBookingSuccess?.(result);
                onComplete?.({ ...result, leadId });
              }}
              onError={(err) => {
                console.error('Booking error:', err);
                onError?.(err);
              }}
              onNextStep={handleBookingNextStep}
            />
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Chat state - Compact or Expanded (mobile-optimized)
  // ============================================================
  
  // Determine step type for layout calculations
  const isChoiceStep = currentStep?.type === 'quick_reply';
  // MA-UI-108: Added zip_input to input step types
  const isInputStep = ['text_input', 'phone_input', 'email_input', 'name_input', 'zip_input'].includes(currentStep?.type);
  
  // MA-UI-121: Dynamic response area height based on number of options
  // Each button height matches DS.option.height + gap, plus 24px padding (12px top + 12px bottom)
  const BUTTON_HEIGHT = DS.option.height; // 36px — must match actual rendered chip height
  const BUTTON_GAP = DS.option.gap;       // 8px — must match ReplyChips gap
  const RESPONSE_PADDING = DS.space.md * 2; // 12px top + 12px bottom = 24px
  const MIN_RESPONSE_HEIGHT = 72;  // Minimum for input steps
  const MAX_RESPONSE_HEIGHT = 160; // Maximum for many options
  
  const calculateResponseHeight = () => {
    // When funnel is complete (terminal step), no response area needed
    if (!currentStep) return 0;
    
    // MA-PHONE-CONSENT-037: Check if current step requires consent
    // MA-RUNTIME-CONSENT-LAYOUT-PARITY-IMPLEMENT-057: Use measured consent height instead of hard-coded 240px
    const requiresConsent = currentStep?.requires_consent;
    const CONSENT_PADDING = 16; // Spacing between input and consent block
    
    // Input steps with consent need expanded height based on actual measurement
    if (isInputStep && requiresConsent) {
      return LAYOUT.RESPONSE_AREA_HEIGHT_INPUT + consentBlockHeight + CONSENT_PADDING;
    }
    
    // Regular input steps (no consent)
    if (isInputStep) {
      return LAYOUT.RESPONSE_AREA_HEIGHT_INPUT;
    }
    
    // Choice steps with options
    if (isChoiceStep && currentStep?.options?.length) {
      const numOptions = currentStep.options.length;
      // Calculate exact height needed for buttons
      const buttonsHeight = (numOptions * BUTTON_HEIGHT) + ((numOptions - 1) * BUTTON_GAP) + RESPONSE_PADDING;
      // Clamp between min and max
      return Math.min(Math.max(buttonsHeight, MIN_RESPONSE_HEIGHT), MAX_RESPONSE_HEIGHT);
    }
    
    return LAYOUT.RESPONSE_AREA_HEIGHT_CHOICES;
  };
  
  const responseAreaHeight = calculateResponseHeight();
  
  // STABLE CARD HEIGHT: Total card height remains constant in expanded mode
  // When response area shrinks (input steps), thread area grows to absorb the space
  const expandedCardHeight = LAYOUT.HEADER_HEIGHT + LAYOUT.EXPANDED_TOTAL_CONTENT_HEIGHT;
  
  // Compact mode: auto-size to fit response options cleanly
  // Instead of a fixed 260px card, derive from response needs
  const compactResponseHeight = calculateResponseHeight();
  const compactCardHeight = LAYOUT.HEADER_HEIGHT + LAYOUT.COMPACT_THREAD_HEIGHT + compactResponseHeight;
  
  // Thread height dynamically adjusts: total content - response = thread
  // This ensures when response shrinks, thread grows (and vice versa)
  const dynamicThreadHeight = LAYOUT.EXPANDED_TOTAL_CONTENT_HEIGHT - responseAreaHeight;
  
  // MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Taller card for consent-enabled inputs
  const isConsentEnabledInput = isInputStep && currentStep?.requires_consent && layoutMode === 'expanded';
  const consentEnabledCardHeight = LAYOUT.HEADER_HEIGHT + 600; // Taller to accommodate consent block
  
  // MA-287: RENDER GUARD - Safety check before main render
  console.log('[MA-287][PRE-RENDER CHECK]', {
    currentStep,
    currentStepId,
    messages_length: messages?.length,
    tokens_containerBg: tokens?.containerBg,
    tokens_accent: tokens?.accent,
    loading,
    error
  });
  
  // MA-287: Check if tokens are broken
  if (!loading && !error && (!tokens || !tokens.containerBg)) {
    console.error('[MA-287][BLOCKED] Missing essential tokens!', {
      tokens,
      resolvedLpTokensFromBuilder,
      lpTokens,
      funnelTheme
    });
  }
  
  // MA-288: RENDER CHECK - Right before main return
  console.log('[MA-288][RENDER CHECK BEFORE RETURN]', {
    loading,
    error,
    funnelState,
    currentStep,
    type: currentStep?.type,
    showOptions,
    compactEntryActive,
    layoutMode
  });

  return (
    <>
      <div 
        className={`${layoutMode === 'compact' ? 'py-1 px-2' : 'py-2 px-3'} lp-funnel-themed ${className}`} 
        data-testid="conversation-funnel"
      >
      <TypingStyles />
      <ThemeStyleInjection tokens={tokens} />
      <div ref={containerRef} style={{...containerStyles, background: 'transparent'}}>
        <motion.div 
          style={cardStyles}
          initial={false}
          animate={{ 
            height: layoutMode === 'compact' 
              ? compactCardHeight 
              : (isConsentEnabledInput ? 'auto' : expandedCardHeight)
          }}
          transition={{ 
            duration: isConsentEnabledInput ? 0 : 0.28,
            ease: [0.25, 0.1, 0.25, 1],
          }}
        >
          {/* Region 1: Fixed Header — structural layer, always rendered */}
          <FunnelHeader appearance={appearance} isLpDark={isLpDark} resolvedContext={resolvedContext} tokens={tokens} />
          
          {/* MA-ARCH-187B-REFINEMENT: Compact Entry State */}
          {compactEntryActive && layoutMode === 'compact' ? (
            <div 
              data-testid="compact-entry"
              style={{ 
                height: `${LAYOUT.COMPACT_CARD_TOTAL_HEIGHT - LAYOUT.HEADER_HEIGHT}px`,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: `0 ${DS.space.lg}px`,
                gap: `${DS.space.lg}px`
              }}
            >
              {/* Single invitation message — category: engagement_entry, variant: invitation_compact */}
              <div 
                data-testid="compact-entry-message"
                data-category="engagement_entry"
                data-variant="invitation_compact"
                style={{
                  padding: DS.bubble.padding,
                  maxWidth: DS.bubble.maxWidth,
                  borderRadius: '16px 16px 16px 4px',
                  backgroundColor: tokens.compactMessageBg,
                  color: tokens.compactMessageText,
                  fontSize: DS.text.body.size,
                  fontWeight: DS.text.body.weight,
                  lineHeight: DS.text.body.lineHeight,
                  letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
                }}
              >
                {resolvedContext?.invitation || 'Answer a few quick questions to get started'}
              </div>
              
              {/* Single CTA — triggers expansion */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  data-testid="compact-entry-cta"
                  onClick={() => {
                    setCompactEntryActive(false);
                    setLayoutMode('expanded');
                    setHasUserInteracted(true);
                  }}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '22px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: DS.text.option.size,
                    fontWeight: DS.text.label.weight,
                    letterSpacing: 'var(--ma-typo-tracking-tight, -0.01em)',
                    backgroundColor: tokens.accent,
                    color: tokens.textInverse,
                    transition: 'opacity 0.15s ease',
                    minHeight: '44px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  {resolvedContext?.cta || 'Get Started'}
                </button>
              </div>
            </div>
          ) : (
          <>
          {/* MA-RUNTIME-CONSENT-LAYOUT-APPROVAL-059: Conditional rendering for consent-enabled input steps */}
          {(() => {
            // Use structured layout for consent-enabled inputs (matches builder structure)
            if (isConsentEnabledInput) {
              return renderStructuredConsentLayout();
            }
            
            // Use existing animated layout for all other steps
            return (
              <>
              {/* Region 2: Message Thread - DYNAMIC height that grows when response shrinks */}
              <motion.div 
            ref={threadRef}
            className="overflow-y-auto overflow-x-hidden px-4"
            initial={false}
            animate={{ 
              height: layoutMode === 'compact' 
                ? LAYOUT.COMPACT_THREAD_HEIGHT 
                : dynamicThreadHeight 
            }}
            transition={{ duration: 0.26, ease: [0.25, 0.1, 0.25, 1] }}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              backgroundColor: tokens.threadBg,
            }}
          >
            {/* Bottom-anchored content — MA-ARCH-187 spacing system */}
            <div 
              className="flex flex-col justify-end py-3"
              style={{ minHeight: '100%', gap: `${DS.space.md}px` }}
            >
              {/* Messages - no AnimatePresence to prevent flutter during layout changes */}
              {messages.map((msg) => (
                msg.type === 'agent' 
                  ? <AgentMessage key={msg.id} message={msg.message} />
                  : <UserMessage 
                      key={msg.id} 
                      message={msg.message} 
                      userColor={appearance.userColor}
                      userTextColor={appearance.userTextColor}
                    />
              ))}
              {/* Transitioning message - smooth typing → text transition */}
              {transitioningMessage && (
                <TransitioningAgentMessage 
                  key={transitioningMessage.id}
                  message={transitioningMessage.message}
                  isTyping={transitioningMessage.isTyping}
                />
              )}
              <div ref={messagesEndRef} />
            </div>
          </motion.div>

          {/* Region 3: Response Area - Dynamic height based on step type */}
          {(() => {
            // In compact mode, response fills from thread to bottom
            // In expanded mode, use dynamic height based on number of options
            const actualResponseHeight = layoutMode === 'compact' 
              ? compactResponseHeight 
              : responseAreaHeight;
            
            return (
              <motion.div 
                className={`lp-df-response-area relative`}
                initial={false}
                animate={{ 
                  height: actualResponseHeight
                }}
                transition={{ duration: 0.26, ease: [0.25, 0.1, 0.25, 1] }}
                style={{ 
                  flexShrink: 0,
                  overflow: 'hidden',
                  minHeight: actualResponseHeight,
                  // MA-305: Light mode uses semantic responseBg token (no computed colors)
                  // Dark mode uses legacy getContrastingShade for backward compatibility
                  backgroundColor: tokens.surfaceMode === 'light' 
                    ? (tokens.responseBg || tokens.containerBg)
                    : (tokens.containerBg?.startsWith('#')
                        ? getContrastingShade(tokens.containerBg)
                        : tokens.containerBg),
                  // MA-305: Light mode adds structural divider (Phase 0 parity)
                  borderTop: tokens.surfaceMode === 'light' ? '1px solid rgba(0, 0, 0, 0.06)' : 'none',
                }}
              >
                {/* Scroll down indicator - shows at bottom when more content below */}
                <AnimatePresence>
                  {showScrollDown && (
                    <ScrollIndicator 
                      show={true} 
                      direction="down" 
                      position="bottom"
                      onTap={scrollResponseToBottom}
                      tokens={tokens}
                    />
                  )}
                </AnimatePresence>
                
                {/* Scroll up indicator - shows at top when scrolled down */}
                <AnimatePresence>
                  {showScrollUp && (
                    <ScrollIndicator 
                      show={true} 
                      direction="up" 
                      position="top"
                      onTap={scrollResponseToTop}
                      tokens={tokens}
                    />
                  )}
                </AnimatePresence>
                
                {/* Bottom gradient fade when more content below */}
                <AnimatePresence>
                  {showScrollDown && <GradientFade show={true} position="bottom" tokens={tokens} />}
                </AnimatePresence>
                
                {/* Top gradient fade when scrolled down */}
                <AnimatePresence>
                  {showScrollUp && <GradientFade show={true} position="top" tokens={tokens} />}
                </AnimatePresence>
                
                {/* Scrollable content area — MA-ARCH-187 consistent rhythm */}
                <div
                  ref={responseAreaRef}
                  className="h-full"
                  style={{ 
                    overflowY: isChoiceStep ? 'auto' : 'hidden',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    height: '100%',
                    padding: `${DS.space.md}px ${DS.space.lg}px`,
                  }}
                  onScroll={checkScrollIndicators}
                >
                  {renderResponseContent()}
                </div>
              </motion.div>
            );
          })()}
          </>
          );  // End of isConsentEnabledInput conditional
          })()}
          </>
          )}
        </motion.div>
      </div>
      
      {/* MA-298/299 DEBUG - COMPACT */}
      <div style={{
        position: 'fixed',
        top: 0,
        right: 0,
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: '#0f0',
        padding: '4px 8px',
        fontSize: '9px',
        fontFamily: 'monospace',
        zIndex: 99999,
        maxWidth: '220px',
        lineHeight: '1.3',
        borderBottomLeftRadius: '4px'
      }}>
        <div style={{fontWeight: 'bold', fontSize: '10px', marginBottom: '2px'}}>MA-298/299</div>
        <div>thread: {tokens.threadBg}</div>
        <div>bubble: {tokens.bubbleAgentBg}</div>
        <div style={{marginTop: '3px', paddingTop: '3px', borderTop: '1px solid #333'}}>
          <div>header: {tokens.headerBg}</div>
          <div>response: {tokens.responseBg || (tokens.containerBg?.startsWith('#') ? getContrastingShade(tokens.containerBg) : tokens.containerBg)}</div>
        </div>
        <div style={{
          marginTop: '3px', 
          paddingTop: '3px', 
          borderTop: '1px solid #333',
          color: tokens.headerBg === (tokens.responseBg || tokens.containerBg) ? '#0f0' : '#f00',
          fontWeight: 'bold'
        }}>
          {tokens.headerBg === (tokens.responseBg || tokens.containerBg) ? '✓ MATCH' : '✗ MISMATCH'}
        </div>
      </div>
      
      </div>
    </>
  );
}
